/*
 * Live portal adapter — Mudit's shipped /portal/* read layer (SKY-440/441),
 * with graceful fallback to demo data when the backend is unreachable or the
 * workspace is empty (local dev without docker, un-stitched accounts).
 *
 * Live + verified on dev (2026-07-21, as a real client login):
 *   GET /portal/me                          → workspace + permissions + stepper booleans
 *   GET /portal/campaigns                   → workspace-scoped projects + legs + stats
 *   GET /portal/campaigns/{id}/replies      → campaign_replies read path
 *
 * UNVERIFIED until first live replies land (rate-limited at stitch time —
 * refills on the 2h sync cadence): exact reply sender field names. The mapper
 * below accepts sender_name / sender_email / display_name defensively.
 */
import { LruCache } from '../../utils/lruCache';
import { httpClient } from '../../lib/httpClient';
import {
  safeHttpUrl,
  stripHtmlToTextWithBreaks,
  stripWrappingQuotes,
  trimQuotedReply,
} from '../../utils/sanitization';
import { containsHtml, sanitizeEmailHtml } from '../../utils/emailHtml';
import type {
  LaunchSummary,
  LeadCopyPeoplePage,
  LeadCopyPerson,
  LeadCopyStatus,
  EventGoing,
  LeadCopyStatusCounts,
  SignOff,
  TodayQueuePage,
  CompanyDescription,
  LeadCopyTrackSummary,
  AccountCorrection,
  AccountStage,
  AccountsPage,
  AttendeeAdditionResult,
  AttendeeFacets,
  NewAttendee,
  AttendeeQuery,
  AttendeesPage,
  CampaignLead,
  CampaignBrief,
  CampaignPauseEvent,
  CampaignReply,
  CampaignSequence,
  CampaignStatsHistory,
  ClaudeCredential,
  ClaudeCredentialStatus,
  CompaniesPage,
  CompanyFacets,
  CompanyQuery,
  ConferenceChoice,
  ConferenceOption,
  SignupSummary,
  CopyVariant,
  EngagementRule,
  EngagementStep,
  EventFilters,
  EventLookupInput,
  EventSort,
  EventsPage,
  LeadDisposition,
  LeadList,
  OutreachChannel,
  OutreachEnrollResult,
  OutreachSender,
  OutreachSequence,
  OutreachSequenceState,
  OutreachSettingsInput,
  OutreachSettingsResult,
  OutreachStepInput,
  PersonDetail,
  PersonLastTouch,
  PipelineStage,
  PortalAccountDetail,
  PortalAttendee,
  PortalAttendeeSource,
  PortalCampaign,
  PortalCompany,
  PortalEvent,
  PortalLoginInput,
  PortalLoginSaved,
  PortalLoginStatus,
  PortalWorkspace,
  RunOutcome,
  SendHistoryPage,
  SendHistoryQuery,
  SendingSettings,
  TamCounts,
  TamFilters,
  TimelineMessage,
  TodayQueueBulkResult,
  UploadListResult,
  DinnerBrief,
  DinnerGuestPage,
  DinnerUpdate,
  DinnerMode,
  EventPlatform,
  CalendarStatus,
  GuestCounts,
  GuestOrigin,
  GuestStatus,
  CreditEstimate,
  CreditUsagePage,
  CreditUsageRow,
  ResearchCredits,
  WorkspaceCredits,
  CurrentPlan,
  PlanOption,
  TopUpPack,
  WorkspacePlan,
  PaymentMethod,
} from './types';
import { mockOutreachSequence, portalApi as demoApi } from './mocks';
import { CLAUDE_CREDENTIAL_STATUSES, EVENT_HOST_STATUSES, GUEST_STATUSES } from './types';
import type { TimelineActivity } from '../../types/accounts';
import type { BriefFieldName } from '../../constants/brief';
import type { CampaignBrief as ApiCampaignBrief } from '../../types/pipeline';
import type { ReplyTone } from './types';

export type DataSource = 'live' | 'demo';

/** The sign-up link existed and is spent, revoked, expired or names a dropped conference. */
export class SignupLinkDeadError extends Error {}

/** The feed reads as sentences, not event types ("Dana replied" vs reply_received). */
const feedSentence = (activityType: string, name: string | null): string => {
  switch (activityType) {
    case 'email_sent': return name ? `Email to ${name}` : 'Email sent';
    case 'email_received': return name ? `Email from ${name}` : 'Email received';
    case 'reply_received': return name ? `${name} replied` : 'Reply received';
    case 'meeting_held': return name ? `Meeting with ${name}` : 'Meeting held';
    case 'linkedin_invite_sent': return name ? `LinkedIn invite to ${name}` : 'LinkedIn invite sent';
    case 'linkedin_connected': return name ? `${name} connected on LinkedIn` : 'LinkedIn connection';
    case 'linkedin_message_sent': return name ? `LinkedIn message to ${name}` : 'LinkedIn message sent';
    case 'client_correction': return 'You corrected the brief';
    default: return activityType.replace(/_/g, ' ');
  }
};

// Dev-only demo override: `localStorage.setItem('portal_demo', '1')` + reload
// forces the mock adapter without touching the backend; remove the key (or
// build for prod) to restore live. Read once at module load; every read method
// short-circuits to the demo adapter when set.
const FORCE_DEMO = import.meta.env.DEV && localStorage.getItem('portal_demo') === '1';

/** True only for the dev-only forced-demo override above — callers that must
 * distinguish "demo because forced" from "demo because live failed" use this
 * (SKY-507: a real unprovisioned account must get the waitlist, not the demo
 * portal, but the forced override keeps showing the full demo experience). */
export const isDemoForced = (): boolean => FORCE_DEMO;

export const CAMPAIGNS_PAGE_SIZE = 20;
export type CampaignStage = 'all' | 'live' | 'prep' | 'review' | 'done';
/** Mirror of the server-side tab filter, for the demo adapter only. */
export const matchesCampaignStage = (c: PortalCampaign, stage: CampaignStage): boolean =>
  stage === 'all' ? true
  : stage === 'live' ? ['live', 'replies'].includes(c.pipelineStage)
  : stage === 'prep' ? !['live', 'replies', 'done'].includes(c.pipelineStage)
  : stage === 'review' ? c.needsClientReview
  : c.pipelineStage === 'done';

/** /portal/tam page size — the UI treats a full page as "more may exist".
 * 50 keeps pages fast (each also derives last-touch fields server-side) and
 * makes the Load-more control actually appear on real-sized lists. */
export const TAM_PAGE_SIZE = 50;
export const DINNER_GUEST_PAGE = 100;
export const ACCOUNTS_PAGE_SIZE = 50;
export const EVENTS_PAGE_SIZE = 30;
export const RUN_POLL_MS = 4000;
// Attendee rows are a flat table and feed the CSV export's page loop, so page at
// the backend maximum (limit<=100) rather than the card grid's size.
export const ATTENDEES_PAGE_SIZE = 100;
const attendeeSources = new LruCache<PortalAttendeeSource>(500);
/** Provenance is per scrape: drop it when the roster is fetched again. */
export const clearAttendeeSourceCache = (): void => attendeeSources.clear();

/** Repeated keys for list filters, flags only when set, so the backend sees one value per key. */
const listParams = (
  query: { page?: number; limit?: number; sort?: string; q?: string },
  lists: Record<string, string[] | undefined>,
  flags: Record<string, boolean | null | undefined>,
): URLSearchParams => {
  const params = new URLSearchParams({ page: String(query.page ?? 1), limit: String(query.limit ?? ATTENDEES_PAGE_SIZE) });
  if (query.sort) params.set('sort', query.sort);
  if (query.q?.trim()) params.set('q', query.q.trim());
  Object.entries(lists).forEach(([key, values]) => values?.forEach((v) => params.append(key, v)));
  Object.entries(flags).forEach(([key, value]) => {
    if (typeof value === 'boolean') params.set(key, String(value));
  });
  return params;
};
// Smaller: feed rows expand inline, so a page has to fit the card's scroll area.
export const FEED_PAGE_SIZE = 20;
// The runner shares four interactive slots, so a chip can sit queued a while.
const REWRITE_POLL_MS = 1500;
const REWRITE_TIMEOUT_MS = 120_000;

// Derived, not written: 'live' iff the workspace was reachable within the cache TTL.
// Removes the shared-mutable write race across concurrent calls (PR #568 review) —
// the badge reflects workspace reachability, which is the honest signal.
export const getDataSource = (): DataSource =>
  !FORCE_DEMO && meCache && Date.now() - meCacheAt < ME_CACHE_TTL_MS ? 'live' : 'demo';

interface ApiPortalMe {
  workspace_id: string;
  name: string | null;
  permissions: { approve_gates: boolean; draft_replies: boolean; curate_tam: boolean };
  intake_done: boolean;
  tam_built: boolean;
  campaign_prep: boolean;
  campaign_live: boolean;
  /** The same fact as campaign_prep, minus its degrade-to-false: null when the read
   * failed, absent on a backend that predates it. Never narrow this to boolean. */
  has_project?: boolean | null;
  /** A tam/copy step read failed; absent on a backend that predates it. */
  steps_unknown?: boolean;
  sources_being_verified?: string[] | null;
  setup_declined?: boolean;
}

interface ApiTodayQueuePage {
  state?: 'fresh' | 'being_verified';
  rows?: ApiTodayQueueItem[];
  truncated?: boolean;
}

interface ApiTodayQueueItem {
  id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  channel: string;
  person: string | null;
  title: string | null;
  company: string | null;
  next_step: string;
  reason: string;
  status: string;
  in_capacity: boolean;
  holdable?: boolean;
  simulated?: boolean;
  acknowledged_at?: string | null;
}

interface ApiDinnerBrief {
  name: string;
  mode: DinnerMode;
  platform?: EventPlatform | null;
  event_url?: string | null;
  starts_at?: string | null;
  timezone?: string | null;
  venue_name?: string | null;
  address?: string | null;
  seats?: number | null;
  confirm_cap?: number | null;
  host_user_id?: string | null;
  calendar_status?: CalendarStatus | null;
  calendar_synced_at?: string | null;
  guests_synced_at?: string | null;
}

const toDinnerBrief = (d: ApiDinnerBrief): DinnerBrief => ({
  name: d.name,
  mode: d.mode,
  platform: d.platform ?? null,
  eventUrl: safeHttpUrl(d.event_url ?? null),
  startsAt: d.starts_at ?? null,
  timezone: d.timezone ?? null,
  venueName: d.venue_name ?? null,
  address: d.address ?? null,
  seats: d.seats ?? null,
  confirmCap: d.confirm_cap ?? null,
  hostUserId: d.host_user_id ?? null,
  calendarStatus: d.calendar_status ?? null,
  calendarSyncedAt: d.calendar_synced_at ?? null,
  guestsSyncedAt: d.guests_synced_at ?? null,
});

interface ApiGuestList {
  counts?: Partial<GuestCounts>;
  guests: {
    entity_id: string;
    name?: string | null;
    company?: string | null;
    status: GuestStatus;
    known_from?: GuestOrigin;
    rsvp_note?: string | null;
    suggested_entity_id?: string | null;
    suggested_name?: string | null;
  }[];
}

interface ApiCampaignSummary {
  id: string;
  name: string;
  pipeline_stage: PipelineStage;
  status?: string;
  needs_client_review: boolean;
  legs: {
    email_campaign_ref: string | null;
    event: { name?: string; date?: string; kind?: string } | null;
    linkedin: {
      account_id: string;
      invited: number;
      accepted: number;
      replied: number;
      messages_sent: number;
      synced_at: string | null;
      state?: string | null;
    } | null;
  };
  stats: {
    sent: number;
    opens: number;
    replies: number;
    meetings: number;
    invited?: number | null;
    accepted?: number | null;
    declined?: number | null;
  };
  stats_synced_at: string | null;
  event_id?: string | null;
  stats_state?: string | null;
  updated_at: string | null;
  lead_count?: number | null;
  reach_count?: number | null;
  left_out_count?: number | null;
  started_at?: string | null;
  sequencer_engine?: 'external' | 'temporal';
}

interface ApiCampaignStatsHistory {
  state: CampaignStatsHistory['state'];
  funnel: CampaignStatsHistory['funnel'];
  sending_starts_on?: string | null;
  sending_starts_in_days?: number | null;
  series: {
    date: string;
    sent: number;
    opens: number;
    replies: number;
    clamped?: boolean;
    spanned_days?: number | null;
    partial?: boolean;
  }[];
  days: number;
}

const toCampaignBrief = (brief: ApiCampaignBrief): CampaignBrief => ({
  fields: brief.fields,
  confirmedAt: brief.confirmed_at,
  confirmedBy: brief.confirmed_by,
  conferenceName: brief.conference_name,
  conferenceStartMissing: brief.conference_start_missing,
  planSummary: brief.plan_summary,
  peopleWanted: brief.people_wanted,
});

interface ApiCreditUsageRow {
  created_at: string;
  campaign_name: string | null;
  action: string;
  label: string | null;
  credits: number;
  exempt: boolean;
}

const toCreditUsageRow = (row: ApiCreditUsageRow): CreditUsageRow => ({
  createdAt: row.created_at,
  campaignName: row.campaign_name,
  action: row.action,
  label: row.label,
  credits: row.credits,
  exempt: row.exempt,
});

interface ApiWorkspacePlan {
  current: {
    plan: PlanOption;
    status: CurrentPlan['status'];
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    scheduled_plan_name: string | null;
  } | null;
  plans: PlanOption[];
  can_manage: boolean;
  exempt: boolean;
  free_credits: number | null;
}

const toWorkspacePlan = (plan: ApiWorkspacePlan): WorkspacePlan => ({
  current: plan.current && {
    plan: plan.current.plan,
    status: plan.current.status,
    periodEnd: plan.current.current_period_end,
    cancelAtPeriodEnd: plan.current.cancel_at_period_end,
    scheduledPlanName: plan.current.scheduled_plan_name,
  },
  plans: plan.plans,
  canManage: plan.can_manage,
  exempt: plan.exempt,
  freeCredits: plan.free_credits,
});

interface ApiLaunchSummary {
  ready: boolean;
  live: boolean;
  waiting_on: 'you' | 'team' | null;
  people: number | null;
  left_out: string;
  senders: string[];
  start_date: string | null;
}

const toLaunchSummary = (launch: ApiLaunchSummary): LaunchSummary => ({
  ready: launch.ready,
  live: launch.live,
  waitingOn: launch.waiting_on,
  people: launch.people,
  leftOut: launch.left_out,
  senders: launch.senders,
  startDate: launch.start_date,
});

interface ApiTamPerson {
  entity_id: string;
  name: string;
  title: string | null;
  company: string | null;
  /** SKY-513: works_at target for the account link */
  company_entity_id?: string | null;
  email?: string | null;
  linkedin_url?: string | null;
  tier: string | null;
  suppressed: boolean;
  last_signal_at: string | null;
  replied: boolean;
  added_at: string | null;
  /** SKY-488: present only when the request filtered by list_id */
  disposition: string | null;
  /** Why the filtering step picked them; list-scoped like disposition */
  match_reason?: string | null;
  provisional?: boolean;
  /** SKY-475 */
  last_touch_at: string | null;
  stale: boolean;
  channels?: string[];
}

interface ApiReply {
  id: string;
  // Wire value is normalized in mapReply — a stale backend or cached response may
  // still carry the legacy vendor value during deploy skew (PR #610 review).
  provider: string;
  thread_id: string | null;
  direction: 'inbound' | 'outbound';
  subject: string | null;
  body: string | null;
  occurred_at: string | null;
  // verified live shape (2026-07-21, EL workspace): sender is a NESTED object
  // (ReplySender: entity join name/title/company + sender_email fallback)
  sender: {
    entity_id: string | null;
    name: string | null;
    title: string | null;
    company: string | null;
    email: string | null;
  } | null;
  read_at?: string | null;
  disposition?: string | null;
  triage_status?: string | null;
  mailbox_email?: string | null;
  from_lead?: boolean;
  cc?: string | null;
  bcc?: string | null;
  triage_in_flight?: boolean;
  draft?: {
    body: string;
    status: string;
    sent_at: string | null;
    dismiss_reason: string | null;
    source?: string;
  } | null;
  // Joined server-side on GET /portal/replies (SKY-563); absent per-campaign.
  campaign_id?: string | null;
  campaign_name?: string | null;
}

const ME_CACHE_TTL_MS = 60_000; // review finding (PR #565): stale singleton — bounded now
let meCache: ApiPortalMe | null = null;
let meCacheAt = 0;
let meInFlight: Promise<ApiPortalMe | null> | null = null; // de-dup concurrent mounts (PR #568 review)
const fetchMe = (): Promise<ApiPortalMe | null> => {
  if (FORCE_DEMO) return Promise.resolve(null);
  if (meCache && Date.now() - meCacheAt < ME_CACHE_TTL_MS) return Promise.resolve(meCache);
  if (meInFlight) return meInFlight;
  meInFlight = (async () => {
    try {
      const res = await httpClient.get<ApiPortalMe>('/portal/me');
      if (res.data?.workspace_id) {
        meCache = res.data;
        meCacheAt = Date.now();
        return meCache;
      }
    } catch { /* fall through */ }
    return null;
  })().finally(() => { meInFlight = null; });
  return meInFlight;
};

const KNOWN_EVENT_KINDS = new Set(['conference', 'dinner', 'side-event']);
const eventKind = (k: string | undefined): 'conference' | 'dinner' | 'side-event' =>
  (k && KNOWN_EVENT_KINDS.has(k) ? k : 'conference') as 'conference' | 'dinner' | 'side-event';

const mapCampaign = (p: ApiCampaignSummary): PortalCampaign => ({
  id: p.id,
  name: p.name,
  pipelineStage: p.pipeline_stage,
  // Absent (older backend) or unrecognized → active; only an explicit pause renders paused.
  status: p.status === 'paused' ? 'paused' : 'active',
  needsClientReview: p.needs_client_review,
  legs: {
    emailCampaignRef: p.legs?.email_campaign_ref ?? undefined,
    event: p.legs?.event
      ? { name: p.legs.event.name ?? 'Event', date: p.legs.event.date, kind: eventKind(p.legs.event.kind) }
      : undefined,
    linkedin: p.legs?.linkedin
      ? {
          accountId: p.legs.linkedin.account_id,
          invited: p.legs.linkedin.invited ?? 0,
          accepted: p.legs.linkedin.accepted ?? 0,
          replied: p.legs.linkedin.replied ?? 0,
          messagesSent: p.legs.linkedin.messages_sent ?? 0,
          syncedAt: p.legs.linkedin.synced_at ?? null,
          // Absent (older backend) → fresh; present but unrecognized → withheld:
          // a future backend state means "not confirmed fresh", never "render as real".
          state: p.legs.linkedin.state == null || p.legs.linkedin.state === 'fresh' ? 'fresh' : 'being_verified',
        }
      : undefined,
  },
  stats: {
    sent: p.stats?.sent ?? 0,
    opens: p.stats?.opens ?? 0,
    replies: p.stats?.replies ?? 0,
    meetings: p.stats?.meetings ?? 0,
    invited: p.stats?.invited ?? undefined,
    accepted: p.stats?.accepted ?? undefined,
    declined: p.stats?.declined ?? undefined,
  },
  statsSyncedAt: p.stats_synced_at,
  // Same coercion direction as the LinkedIn leg above: unknown states are withheld.
  statsState: p.stats_state == null || p.stats_state === 'fresh' ? 'fresh' : 'being_verified',
  updatedAt: p.updated_at ?? '',
  leadCount: p.lead_count ?? null,
  reachCount: p.reach_count ?? null,
  leftOutCount: p.left_out_count ?? null,
  startedAt: p.started_at ?? null,
  eventId: p.event_id ?? null,
  sequencerEngine: p.sequencer_engine === 'temporal' ? 'temporal' : 'external',
});

const mapReply = (r: ApiReply): CampaignReply => ({
  id: r.id,
  // Anything that isn't the LinkedIn channel renders as email — vendor-neutral
  // and tolerant of legacy values from a not-yet-deployed backend.
  provider: r.provider === 'linkedin' ? 'linkedin' : 'email',
  threadId: r.thread_id,
  direction: r.direction,
  sender: {
    name:
      r.sender?.name ?? r.sender?.email ??
      (r.direction === 'outbound' ? 'Your Skylarq team' : null),
    title: r.sender?.title ?? null,
    company: r.sender?.company ?? null,
    entityId: r.sender?.entity_id ?? null,
    email: r.sender?.email ?? null,
  },
  subject: r.subject,
  // Email bodies arrive as raw HTML. `body` is the plain-text projection
  // (previews, triage); `bodyHtml` preserves the sender's formatting for the
  // thread view, DOMPurify-sanitized (SKY-464). The DB keeps the raw body.
  // LinkedIn is never HTML — content-sniffing alone would render literal
  // "<p>" tags from a contaminated legacy row as if they were real markup.
  body: r.body ? trimQuotedReply(stripHtmlToTextWithBreaks(r.body)) : null,
  bodyHtml:
    r.provider !== 'linkedin' && r.body && containsHtml(r.body)
      ? sanitizeEmailHtml(r.body)
      : null,
  occurredAt: r.occurred_at,
  read: Boolean(r.read_at),
  disposition: r.disposition ?? null,
  // Unknown future statuses degrade to null → derived triage, never a broken tab.
  triageStatus: r.triage_status === 'handled' ? 'handled' : null,
  mailboxEmail: r.mailbox_email ?? null,
  // Absent on a payload from before the field existed: treat as the lead, which
  // is what every such row already was.
  fromLead: r.from_lead !== false,
  cc: r.cc ?? null,
  bcc: r.bcc ?? null,
  triageInFlight: r.triage_in_flight === true,
  draft: r.draft
    ? {
        body: r.draft.body,
        status: (['draft', 'sent', 'dismissed'].includes(r.draft.status)
          ? r.draft.status
          : 'draft') as 'draft' | 'sent' | 'dismissed',
        sentAt: r.draft.sent_at ?? null,
        dismissReason: r.draft.dismiss_reason ?? null,
        source: r.draft.source === 'auto' ? 'auto' : 'client',
      }
    : null,
});

export type WorkspaceReply = CampaignReply & { campaignId: string; campaignName: string };

/** Whole-workspace conversation rows (SKY-563): pages of inbound replies plus
 * their thread siblings, campaign already joined server-side. Walks until a
 * short inbound page; the cap is a runaway guard, surfaced when hit. */
const fetchWorkspaceRepliesPaged = async (): Promise<{ rows: WorkspaceReply[]; complete: boolean }> => {
  // Bodies arrive as previews now, so a page costs roughly a tenth of what it did.
  const PAGE = 50; // limit/offset count inbound rows only
  const MAX_PAGES = 20;
  // A page is PAGE inbound rows PLUS their thread siblings, so the response row
  // count is a multiple of PAGE. If PostgREST clips it at max-rows the inbound
  // count drops below PAGE and the walk reads that as end-of-data, silently
  // dropping conversations while still reporting complete. Treat a response at
  // the cap as truncation, not as an answer.
  const ROW_CAP = 1000; // stock Supabase db-max-rows (see db_utils.py)
  const all: ApiReply[] = [];
  let complete = true;
  for (let page = 0; page < MAX_PAGES; page++) {
    let res;
    try {
      res = await httpClient.get<ApiReply[]>('/portal/replies', { params: { limit: PAGE, offset: page * PAGE } });
    } catch (error) {
      // A failure past page 0 must not discard already-fetched LIVE replies;
      // partial live wins (the write gates would otherwise fire against demo ids).
      if (page === 0) throw error;
      console.warn('portal replies: pagination interrupted, returning partial results', error);
      complete = false;
      break;
    }
    if (!Array.isArray(res.data)) {
      if (page === 0) throw new Error('unexpected replies payload');
      console.warn('portal replies: unexpected page payload, returning partial results');
      complete = false;
      break;
    }
    all.push(...res.data);
    if (res.data.length >= ROW_CAP) {
      console.warn('portal replies: page hit the row cap, returning partial results');
      complete = false;
      break;
    }
    // A short inbound page means end of data. Documented assumption (PR #746):
    // the server drops unrecognized-provider rows after the page slice, so a
    // hypothetical third channel could shorten a page early. Only email and
    // LinkedIn exist today; revisit if a third provider value ever lands.
    if (res.data.filter((r) => r.direction === 'inbound').length < PAGE) break;
    if (page === MAX_PAGES - 1) {
      console.warn('portal replies: hit MAX_PAGES cap, some replies may be omitted');
      complete = false;
    }
  }
  // A thread whose inbound rows straddle two pages returns its siblings twice.
  const deduped = [...new Map(all.map((r) => [r.id, r])).values()];
  // A 200 IS live data: refresh /portal/me so getDataSource() stays authoritative (PR #569).
  await fetchMe();
  const rows = deduped.map((r) => ({
    ...mapReply(r),
    campaignId: r.campaign_id ?? '',
    campaignName: r.campaign_name ?? 'Campaign',
  }));
  return { rows, complete };
};

const demoWorkspaceReplies = async (): Promise<WorkspaceReply[]> => {
  const cs = await demoApi.getCampaigns();
  const c = cs[0] ?? { id: 'proj_1', name: 'Campaign' };
  return (await demoApi.getReplies(c.id)).map((r) => ({ ...r, campaignId: c.id, campaignName: c.name }));
};

/** Providers whose portal connect is an OAuth redirect (auth-url accepts
 * portal=true and the callback bounces back to /portal/settings). Lives beside
 * getConnectUrl; Integrations (buttons) and Overview (finish-setup row) share it. */
export const OAUTH_CONNECT_PROVIDERS = ['gmail', 'google_calendar', 'slack'] as const;
/** OAuth connects the page offers that are optional: never part of SELF_CONNECT,
 * so the finish-setup ask never nags for them. HubSpot is portal-only — its
 * callback always returns to /portal/settings. */
export const OPTIONAL_OAUTH_CONNECT_PROVIDERS = ['hubspot'] as const;
export type OAuthConnectProvider =
  | (typeof OAUTH_CONNECT_PROVIDERS)[number]
  | (typeof OPTIONAL_OAUTH_CONNECT_PROVIDERS)[number];
/** Everything a client can wire up themselves (SKY-460). LinkedIn is not OAuth —
 * it's the credential login flow (ConnectLinkedInModal) — but it counts the
 * same for the finish-setup ask. Granola stays operator/desktop-managed. */
export const SELF_CONNECT = new Set<string>([...OAUTH_CONNECT_PROVIDERS, 'linkedin']);

const AUTH_URL_PATH: Record<OAuthConnectProvider, string> = {
  gmail: '/gmail/auth-url',
  google_calendar: '/google/calendar/auth-url',
  slack: '/slack/auth-url',
  hubspot: '/crm-credentials/hubspot/auth-url',
};

type ClaudeCredentialWire = {
  id: string;
  account_label: string;
  status: string;
  last_validated_at: string | null;
  created_at: string;
};

const isClaudeStatus = (s: string): s is ClaudeCredentialStatus =>
  (CLAUDE_CREDENTIAL_STATUSES as readonly string[]).includes(s);

// An unrecognised status asks for a reconnect rather than claiming a working link.
const mapClaudeCredential = (w: ClaudeCredentialWire): ClaudeCredential => ({
  id: w.id,
  accountLabel: w.account_label,
  status: isClaudeStatus(w.status) ? w.status : 'invalid',
  lastValidatedAt: w.last_validated_at ?? null,
  createdAt: w.created_at,
});

type NotificationSettingsWire = {
  daily_brief: boolean;
  reply_alerts: boolean;
  progress: boolean;
  email: boolean;
  slack_connected: boolean;
};

const mapNotificationSettings = (
  w: NotificationSettingsWire,
): import('./types').NotificationSettings => ({
  dailyBrief: Boolean(w.daily_brief),
  replyAlerts: Boolean(w.reply_alerts),
  progress: Boolean(w.progress),
  email: Boolean(w.email),
  slackConnected: Boolean(w.slack_connected),
});

export type DisconnectableTool = 'gmail' | 'google_calendar' | 'slack' | 'linkedin' | 'hubspot';

/** A description response, or a throw: a wrong-typed body must not render as "none set". */
const parseCompanyDescription = (
  data: { company_description?: unknown } | undefined,
): CompanyDescription => {
  const companyDescription = data?.company_description;
  if (companyDescription !== null && typeof companyDescription !== 'string') {
    throw new Error('Malformed company description response');
  }
  return { companyDescription };
};

/** A sign-off response, or a throw: a wrong-typed body must never render as "no sign-off". */
const parseSignOff = (data: { sign_off?: unknown } | undefined): SignOff => {
  const signOff = data?.sign_off;
  if (signOff !== null && typeof signOff !== 'string') throw new Error('Malformed sign-off response');
  return { signOff };
};

type OutreachSequenceWire = {
  id: string;
  status: OutreachSequence['status'];
  mode: OutreachSequence['mode'];
  timezone: string;
  window_start: string;
  window_end: string;
  daily_cap: number;
  sender_user_id?: string | null;
  send_weekdays_only?: boolean;
  steps: {
    id: string; position: number; channel: OutreachChannel; delay_days: number;
    subject: string | null; body: string; lead_copy_count?: number | null;
    not_before?: string | null; pending_at?: string | null;
    pending_change?: {
      subject?: string; body?: string; delay_days?: number; not_before?: string | null; with_note?: boolean;
    } | null;
  }[];
  enrollment_counts: OutreachSequence['enrollmentCounts'];
  live_reach?: number;
  written_email_tracks?: number | null;
  stopped_reasons?: Record<string, number>;
  held_reasons?: Record<string, number>;
  paused_reasons?: Record<string, number>;
  finished_reasons?: Record<string, number>;
  failed_reasons?: OutreachSequence['failedReasons'];
  variables?: string[];
  append_signature?: boolean;
  signature_status?: OutreachSequence['signatureStatus'];
  editable: boolean;
  last_send_on?: string | null;
  no_hard_stop?: boolean;
  hard_stop_required?: boolean;
  app_requests?: boolean;
  variable_values?: Record<string, string>;
  warnings?: string[];
};

const mapOutreachSequence = (w: OutreachSequenceWire): OutreachSequence => ({
  id: w.id,
  status: w.status,
  mode: w.mode,
  timezone: w.timezone,
  windowStart: w.window_start,
  windowEnd: w.window_end,
  dailyCap: w.daily_cap,
  senderUserId: w.sender_user_id ?? null,
  sendWeekdaysOnly: w.send_weekdays_only ?? true,
  steps: [...w.steps]
    .sort((a, b) => a.position - b.position)
    .map((st) => ({
      id: st.id, position: st.position, channel: st.channel, delayDays: st.delay_days,
      subject: st.subject, body: st.body, leadCopyCount: st.lead_copy_count ?? null,
      notBefore: st.not_before ?? null, pendingAt: st.pending_at ?? null,
      pendingChange: st.pending_change
        ? {
          subject: st.pending_change.subject, body: st.pending_change.body,
          delayDays: st.pending_change.delay_days, notBefore: st.pending_change.not_before,
          withNote: st.pending_change.with_note,
        }
        : null,
    })),
  enrollmentCounts: w.enrollment_counts,
  liveReach: w.live_reach ?? w.enrollment_counts.active,
  writtenEmailTracks: w.written_email_tracks ?? null,
  stoppedReasons: w.stopped_reasons ?? {},
  heldReasons: w.held_reasons ?? {},
  pausedReasons: w.paused_reasons ?? {},
  finishedReasons: w.finished_reasons ?? {},
  failedReasons: w.failed_reasons ?? {},
  variables: w.variables ?? [],
  appendSignature: w.append_signature ?? true,
  signatureStatus: w.signature_status ?? null,
  editable: w.editable,
  lastSendOn: w.last_send_on ?? null,
  noHardStop: w.no_hard_stop ?? false,
  hardStopRequired: w.hard_stop_required ?? false,
  appRequests: w.app_requests ?? false,
  variableValues: w.variable_values ?? {},
  warnings: w.warnings ?? [],
});

type EventHostWire = {
  site: string;
  name: string;
  status: string;
  saved_at: string | null;
  last_verified_at: string | null;
};

const isEventHostStatus = (s: string): s is import('./types').EventHostStatus =>
  (EVENT_HOST_STATUSES as readonly string[]).includes(s);

// An unrecognised status asks for a connect rather than claiming a live session.
const mapEventHost = (w: EventHostWire): import('./types').EventHostConnection => ({
  site: w.site,
  name: w.name,
  status: isEventHostStatus(w.status) ? w.status : 'not_connected',
  savedAt: w.saved_at ?? null,
  lastVerifiedAt: w.last_verified_at ?? null,
});

const DEMO_EVENT_HOSTS: import('./types').EventHostConnection[] = [
  { site: 'luma.com', name: 'Luma', status: 'not_connected', savedAt: null, lastVerifiedAt: null },
  { site: 'partiful.com', name: 'Partiful', status: 'not_connected', savedAt: null, lastVerifiedAt: null },
  { site: 'eventbrite.com', name: 'Eventbrite', status: 'not_connected', savedAt: null, lastVerifiedAt: null },
];

const outreachUrl = (projectId: string, path = ''): string =>
  `/portal/campaigns/${encodeURIComponent(projectId)}/outreach-sequence${path}`;

const OUTREACH_SETTINGS_WIRE: Record<keyof OutreachSettingsInput, string> = {
  mode: 'mode', timezone: 'timezone', windowStart: 'window_start', windowEnd: 'window_end',
  dailyCap: 'daily_cap', senderUserId: 'sender_user_id', sendWeekdaysOnly: 'send_weekdays_only',
};

interface AttendeeAdditionBody {
  batch_id: string | null;
  added: number;
  already_found: number;
  rejected: number;
  rejects: { row: number | null; reason: string }[];
}

const attendeesAdded = (body: AttendeeAdditionBody | undefined): AttendeeAdditionResult => ({
  batchId: body?.batch_id ?? null,
  added: body?.added ?? 0,
  alreadyFound: body?.already_found ?? 0,
  rejected: body?.rejected ?? 0,
  rejects: body?.rejects ?? [],
});

export const portalApi = {
  /** Auth URL for a client self-connectable OAuth tool (SKY-460). portal=true
   * marks the OAuth state so the callback bounces back to /portal/settings. */
  getConnectUrl: async (provider: OAuthConnectProvider): Promise<string> => {
    // Demo mode must never reach a real provider; '' reads as failure upstream.
    if (FORCE_DEMO) return '';
    // HubSpot's callback always returns to the portal, so it takes no portal flag.
    const params = provider === 'hubspot' ? undefined : { portal: true };
    const res = await httpClient.get<{ auth_url?: string }>(AUTH_URL_PATH[provider], { params });
    return String(res.data?.auth_url ?? '');
  },

  /** LinkedIn connect step 1 (credential login, SKY-460). */
  linkedinLogin: async (email: string, password: string): Promise<import('./types').LinkedInConnectResult> => {
    // A demo walkthrough must never fire a REAL LinkedIn login attempt —
    // repeated auth attempts are what trip LinkedIn security checkpoints.
    if (FORCE_DEMO) return { status: 'success', message: null, checkpointType: null };
    const res = await httpClient.post<{ status?: string; message?: string; checkpoint_type?: string }>(
      '/linkedin/auth/login', { email, password },
    );
    return {
      status: String(res.data?.status ?? 'unknown'),
      message: res.data?.message ?? null,
      checkpointType: res.data?.checkpoint_type ?? null,
    };
  },

  /** LinkedIn connect step 2 — the checkpoint code LinkedIn sent. */
  linkedinVerifyCode: async (email: string, code: string): Promise<import('./types').LinkedInConnectResult> => {
    if (FORCE_DEMO) return { status: 'success', message: null, checkpointType: null };
    const res = await httpClient.post<{ status?: string; message?: string; checkpoint_type?: string }>(
      '/linkedin/auth/verify-code', { email, code },
    );
    return {
      status: String(res.data?.status ?? 'unknown'),
      message: res.data?.message ?? null,
      checkpointType: res.data?.checkpoint_type ?? null,
    };
  },

  /** SKY-561: client pastes their own sending key; backend validates it live before storing. */
  instantlyConnect: async (apiKey: string): Promise<void> => {
    if (FORCE_DEMO) return; // never send a real key from a demo walkthrough
    await httpClient.post('/instantly/connect', { api_key: apiKey });
  },

  instantlyDisconnect: async (): Promise<void> => {
    if (FORCE_DEMO) return;
    await httpClient.post('/instantly/disconnect', {});
  },

  /** Disconnect a client-connected tool. Calendar can only delete per
   * connection, so it lists and deletes each. */
  disconnectTool: async (provider: DisconnectableTool): Promise<void> => {
    if (FORCE_DEMO) return;
    if (provider === 'linkedin') {
      // Scoped to the one seat the Settings row disclosed (the first active,
      // the same rule the integrations read applies) — an unscoped disconnect
      // would sever every seat. Provider-side deletion is best-effort in the
      // backend; local deactivation happens regardless.
      const res = await httpClient.get<{ connections: { id: string; is_active: boolean }[] }>('/linkedin/credentials/list');
      const active = (res.data?.connections ?? []).find((c) => c.is_active);
      if (!active) return;
      await httpClient.post('/linkedin/credentials/disconnect', { credential_id: active.id });
    } else if (provider === 'gmail') {
      const res = await httpClient.delete<{ success: boolean; message?: string }>('/gmail/disconnect');
      // The endpoint reports failure as 200 with success:false, not a status.
      if (res.data?.success === false) throw new Error(res.data.message || 'gmail disconnect failed');
    } else if (provider === 'slack') {
      await httpClient.delete('/slack/disconnect');
    } else if (provider === 'hubspot') {
      // Severs the caller's own HubSpot credential; 409 when another member owns it.
      await httpClient.delete('/crm-credentials/hubspot');
    } else {
      const res = await httpClient.get<{ connections: { id: string }[] }>('/google/calendar/connections');
      const connections = res.data?.connections ?? [];
      // Attempt every connection so one failure can't strand the rest half-disconnected.
      const results = await Promise.allSettled(
        connections.map((c) => httpClient.delete(`/google/calendar/disconnect/${c.id}`)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        throw new Error(`${failed} of ${connections.length} calendar connections could not be disconnected`);
      }
    }
  },


  /** null is the real "nothing connected" state; a failed read throws. */
  getClaudeCredential: async (): Promise<ClaudeCredential | null> => {
    if (FORCE_DEMO) return null;
    const res = await httpClient.get<ClaudeCredentialWire | null>('/agent-credentials/claude-code');
    return res.data ? mapClaudeCredential(res.data) : null;
  },

  /** Stores the setup token and returns the row as saved (pending validation);
   * the backend verifies it in the background. */
  claudeConnect: async (token: string): Promise<ClaudeCredential | null> => {
    if (FORCE_DEMO) return null;
    const res = await httpClient.put<{ credential: ClaudeCredentialWire }>(
      '/agent-credentials/claude-code',
      { token, account_label: 'Claude Code' },
    );
    return res.data?.credential ? mapClaudeCredential(res.data.credential) : null;
  },

  claudeDisconnect: async (): Promise<void> => {
    if (FORCE_DEMO) return;
    await httpClient.delete('/agent-credentials/claude-code');
  },

  // Throws on load failure so the caller can distinguish "failed" from "nothing
  // connected" — an empty array here is a REAL zero-integrations state.
  getIntegrations: async (): Promise<import('./types').PortalIntegration[]> => {
    if (FORCE_DEMO) return demoApi.getIntegrations();
    const res = await httpClient.get('/portal/integrations');
    const rows = Array.isArray(res.data) ? res.data : [];
    return rows.map((r: Record<string, unknown>) => ({
      provider: String(r.provider),
      name: String(r.name),
      connected: Boolean(r.connected),
      account: (r.account as string) ?? null,
      status: String(r.status),
      connectedAt: (r.connected_at as string) ?? null,
      lastSyncedAt: (r.last_synced_at as string) ?? null,
      ownedByViewer: typeof r.owned_by_viewer === 'boolean' ? r.owned_by_viewer : null,
      connectedBy: (r.connected_by as string) ?? null,
    }));
  },

  /** Event-host accounts (Luma, Partiful, Eventbrite) and this workspace's session on each. */
  getEventHosts: async (): Promise<import('./types').EventHostConnection[]> => {
    if (FORCE_DEMO) return DEMO_EVENT_HOSTS;
    const res = await httpClient.get<EventHostWire[]>('/portal/integrations/event-hosts');
    if (!Array.isArray(res.data)) throw new Error('event hosts: unexpected response');
    return res.data.map(mapEventHost);
  },

  /** Opens the host's sign-in page for a takeover; resolves to the takeover page path. */
  connectEventHost: async (site: string): Promise<string> => {
    if (FORCE_DEMO) throw new Error('demo mode has no browser behind Connect');
    const res = await httpClient.post<{ takeover_path: string }>(
      `/portal/integrations/event-hosts/${encodeURIComponent(site)}/connect`,
    );
    return res.data.takeover_path;
  },

  checkEventHost: async (site: string): Promise<import('./types').EventHostConnection> => {
    if (FORCE_DEMO) throw new Error('demo mode has no session to check');
    const res = await httpClient.get<EventHostWire>(
      `/portal/integrations/event-hosts/${encodeURIComponent(site)}/status`,
    );
    return mapEventHost(res.data);
  },

  getNotificationSettings: async (): Promise<import('./types').NotificationSettings> => {
    if (FORCE_DEMO) return demoApi.getNotificationSettings();
    const res = await httpClient.get<NotificationSettingsWire>('/portal/notifications');
    return mapNotificationSettings(res.data);
  },

  updateNotificationSettings: async (
    changes: { dailyBrief?: boolean; replyAlerts?: boolean; progress?: boolean; email?: boolean },
  ): Promise<import('./types').NotificationSettings> => {
    if (FORCE_DEMO) return { ...(await demoApi.getNotificationSettings()), ...changes };
    const body: Record<string, boolean> = {};
    if (changes.dailyBrief !== undefined) body.daily_brief = changes.dailyBrief;
    if (changes.replyAlerts !== undefined) body.reply_alerts = changes.replyAlerts;
    if (changes.progress !== undefined) body.progress = changes.progress;
    if (changes.email !== undefined) body.email = changes.email;
    const res = await httpClient.put<NotificationSettingsWire>('/portal/notifications', body);
    return mapNotificationSettings(res.data);
  },

  /** Persist "don't ask again" for the Overview finish-setup row. Write: throws. */
  setSetupDeclined: async (declined: boolean): Promise<void> => {
    if (FORCE_DEMO) return;
    await httpClient.put('/portal/setup-declined', { declined });
    // The cached /portal/me still carries the old flag; drop it so the next
    // mount reads the persisted state instead of resurrecting the nag.
    meCache = null;
  },

  getWorkspace: async (): Promise<PortalWorkspace> => {
    const me = await fetchMe();
    if (me) {
      return {
        workspaceId: me.workspace_id,
        name: me.name ?? 'Your workspace',
        permissions: {
          approveGates: me.permissions?.approve_gates ?? true,
          draftReplies: me.permissions?.draft_replies ?? true,
          curateTam: me.permissions?.curate_tam ?? true,
        },
        sourcesBeingVerified: me.sources_being_verified ?? [],
        setupDeclined: me.setup_declined === true,
        stepsUnknown: me.steps_unknown === true,
      };
    }
    return demoApi.getWorkspace();
  },

  getEngagementSteps: async (): Promise<EngagementStep[]> => {
    const me = await fetchMe();
    if (me) {
      return [
        { key: 'intake', label: 'Intake call', status: me.intake_done ? 'done' : 'in_progress' },
        { key: 'tam', label: 'Building your target list', status: me.tam_built ? 'done' : me.intake_done ? 'in_progress' : 'pending' },
        { key: 'first_campaign', label: 'First campaign prep', status: me.campaign_prep ? 'done' : me.tam_built ? 'in_progress' : 'pending' },
        { key: 'campaign_live', label: 'Campaign live', status: me.campaign_live ? 'done' : me.campaign_prep ? 'in_progress' : 'pending' },
      ];
    }
    return demoApi.getEngagementSteps();
  },

  /** Whether the workspace already has a project — null when we could not find out,
   * so a caller never reads "unknown" as "none". Deliberately not campaign_prep: that
   * one reports false for a failed read, which would reopen a passed signup step. */
  hasProject: async (): Promise<boolean | null> => {
    if (FORCE_DEMO) return true;
    const me = await fetchMe();
    return me?.has_project ?? null;
  },

  /** Add a CSV of attendees to this workspace's list for the event. Throws. */
  uploadEventAttendees: async (eventId: string, file: File): Promise<AttendeeAdditionResult> => {
    if (FORCE_DEMO) return demoApi.uploadEventAttendees(eventId, file);
    const form = new FormData();
    form.append('file', file);
    const res = await httpClient.post<AttendeeAdditionBody>(
      `/portal/events/${eventId}/attendees/upload`,
      form,
      // axios drops FormData under the JSON default; force multipart (see uploadPeopleCsv).
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return attendeesAdded(res.data);
  },

  /** Add one attendee to this workspace's own list for the event. Throws. */
  addEventAttendee: async (eventId: string, person: NewAttendee): Promise<AttendeeAdditionResult> => {
    if (FORCE_DEMO) return demoApi.addEventAttendee(eventId, person);
    const res = await httpClient.post<AttendeeAdditionBody>(`/portal/events/${eventId}/attendees`, {
      full_name: person.fullName,
      org_name: person.orgName,
      title: person.title || null,
      linkedin_url: person.linkedinUrl || null,
      email: person.email || null,
      role: person.role,
      evidence_url: person.evidenceUrl || null,
    });
    return attendeesAdded(res.data);
  },

  /** Undo one batch this workspace added; how many rows went. Throws. */
  undoEventAttendeeBatch: async (eventId: string, batchId: string): Promise<number> => {
    if (FORCE_DEMO) return demoApi.undoEventAttendeeBatch(eventId, batchId);
    const res = await httpClient.delete<{ removed: number }>(
      `/portal/events/${eventId}/attendees/batches/${batchId}`,
    );
    return res.data?.removed ?? 0;
  },

  /** The conference to open, or null when the account has no link (404). Throws
   * SignupLinkDeadError on a dead link (410), and the error otherwise. */
  claimSignupLink: async (): Promise<string | null> => {
    try {
      const res = await httpClient.post<{ event_id: string }>('/portal/signup-links/claim', {});
      return res.data?.event_id ?? null;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      if (status === 410) throw new SignupLinkDeadError();
      throw error;
    }
  },

  /** Signup step: the shared conference catalog by name. Throws on failure — an
   * empty list means "not in the catalog" and is the cue to add one. */
  searchConferences: async (q: string): Promise<ConferenceOption[]> => {
    const res = await httpClient.get<
      {
        id: string;
        name: string;
        city: string | null;
        country: string | null;
        date_start: string | null;
        date_end: string | null;
      }[]
    >('/portal/signup/conferences', { params: { q } });
    return (res.data ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      city: c.city,
      country: c.country,
      dateStart: c.date_start,
      dateEnd: c.date_end,
    }));
  },

  /** Signup step: the workspace's one intake project, linked to the chosen conference. */
  startConferenceProject: async (choice: ConferenceChoice): Promise<void> => {
    await httpClient.post('/portal/signup/conference', {
      ...(choice.eventId ? { event_id: choice.eventId } : {}),
      ...(choice.name ? { name: choice.name } : {}),
      ...(choice.city ? { city: choice.city } : {}),
      ...(choice.dateStart ? { date_start: choice.dateStart } : {}),
      ...(choice.dateEnd ? { date_end: choice.dateEnd } : {}),
    });
    // The cached /portal/me still says the workspace has no project; drop it so
    // the gate sees the one this call just made.
    meCache = null;
  },

  /** The waiting signup's own answers plus review and brief progress. Live only: throws on failure. */
  /** Drops the cached /portal/me so the next read sees a just-provisioned workspace. */
  forgetWorkspaceStatus: (): void => {
    meCache = null;
  },

  getSignupSummary: async (): Promise<SignupSummary> => {
    const res = await httpClient.get<{
      company_name: string | null;
      company_website: string | null;
      role: string | null;
      conference: { name: string; city: string | null; date_start: string | null; date_end: string | null } | null;
      brief_status: SignupSummary['briefStatus'] | null;
      approved: boolean;
    }>('/portal/signup/summary');
    const d = res.data;
    return {
      companyName: d.company_name,
      companyWebsite: d.company_website,
      role: d.role,
      conference: d.conference
        ? { name: d.conference.name, city: d.conference.city, dateStart: d.conference.date_start, dateEnd: d.conference.date_end }
        : null,
      briefStatus: d.brief_status ?? 'not_started',
      approved: d.approved === true,
    };
  },

  getEvents: async (
    q?: string,
    sort: EventSort = 'date',
    page = 1,
    filters: EventFilters = {},
  ): Promise<EventsPage> => {
    if (FORCE_DEMO) return demoApi.getEvents(q, sort, page, filters);
    const res = await httpClient.get<{
      total: number;
      items: {
        id: string; name: string; date_start: string | null; date_end: string | null;
        city: string | null; country: string | null; venue: string | null;
        vertical: string | null; event_type: string | null;
        categories: string[] | null; event_format: string | null;
        attendee_count: number | null; confirmed_count: number | null;
      }[];
    }>('/portal/events', {
      params: {
        page,
        limit: EVENTS_PAGE_SIZE,
        sort,
        ...(q?.trim() ? { q: q.trim() } : {}),
        ...(filters.kind ? { event_type: filters.kind } : {}),
        ...(filters.format ? { event_format: filters.format } : {}),
        ...(filters.category ? { category: filters.category } : {}),
        ...(filters.place ? { place: filters.place } : {}),
        ...(filters.dateFrom ? { date_from: filters.dateFrom } : {}),
        ...(filters.dateTo ? { date_to: filters.dateTo } : {}),
      },
    });
    return {
      total: res.data?.total ?? 0,
      items: (res.data?.items ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        dateStart: c.date_start,
        dateEnd: c.date_end,
        city: c.city,
        country: c.country,
        venue: c.venue,
        vertical: c.vertical,
        eventType: c.event_type,
        categories: c.categories,
        eventFormat: c.event_format,
        attendeeCount: c.attendee_count,
        confirmedCount: c.confirmed_count,
      })),
    };
  },

  // null = 404 (unknown event); other failures throw to the page's error state.
  getEvent: async (id: string): Promise<PortalEvent | null> => {
    if (FORCE_DEMO) return demoApi.getEvent(id);
    try {
      const res = await httpClient.get<{
        id: string; name: string; date_start: string | null; date_end: string | null;
        city: string | null; country: string | null; venue: string | null;
        vertical: string | null; event_type: string | null;
        categories: string[] | null; event_format: string | null;
        attendee_count: number | null; confirmed_count: number | null;
        project_id: string | null;
      }>(`/portal/events/${id}`);
      const e = res.data;
      if (!e) return null;
      return {
        id: e.id, name: e.name, dateStart: e.date_start, dateEnd: e.date_end,
        city: e.city, country: e.country, venue: e.venue, vertical: e.vertical,
        eventType: e.event_type, categories: e.categories, eventFormat: e.event_format,
        attendeeCount: e.attendee_count, confirmedCount: e.confirmed_count,
        projectId: e.project_id,
      };
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
      throw error;
    }
  },

  getEventAttendees: async (id: string, query: AttendeeQuery = {}): Promise<AttendeesPage> => {
    if (FORCE_DEMO) return demoApi.getEventAttendees(id, query.page ?? 1);
    const params = listParams(query, { role: query.role, grade: query.grade }, { has_linkedin: query.hasLinkedin });
    const res = await httpClient.get<{
      total: number;
      items: {
        id: string; full_name: string | null; title: string | null; org_name: string | null;
        linkedin_url: string | null; confidence_grade: 'confirmed' | 'likely' | 'inferred' | null;
        role: string; added_by_team: boolean; addition_status: string | null;
      }[];
    }>(`/portal/events/${id}/attendees`, { params });
    return {
      total: res.data?.total ?? 0,
      items: (res.data?.items ?? []).map(
        (a): PortalAttendee => ({
          id: a.id,
          fullName: a.full_name,
          title: a.title,
          orgName: a.org_name,
          linkedinUrl: safeHttpUrl(a.linkedin_url),
          confidenceGrade: a.confidence_grade ?? null,
          role: a.role,
          addedByTeam: a.added_by_team ?? false,
          additionStatus: a.addition_status ?? null,
        }),
      ),
    };
  },

  getEventAttendeeFacets: async (id: string): Promise<AttendeeFacets> => {
    if (FORCE_DEMO) return { role: {}, grade: {}, linkedin: {} };
    const res = await httpClient.get<AttendeeFacets>(`/portal/events/${id}/attendees/facets`);
    return { role: res.data?.role ?? {}, grade: res.data?.grade ?? {}, linkedin: res.data?.linkedin ?? {} };
  },

  getAttendeeSource: async (eventId: string, attendanceId: string): Promise<PortalAttendeeSource> => {
    if (FORCE_DEMO) return { method: null, mode: null, url: null, evidence: null, provider: 'demo', foundAt: null };
    const cached = attendeeSources.get(attendanceId);
    if (cached) return cached;
    const res = await httpClient.get<{
      method: string | null; mode: string | null; url: string | null;
      evidence: string | null; provider: string | null; found_at: string | null;
    }>(`/portal/events/${eventId}/attendees/${attendanceId}/source`);
    const d = res.data;
    const source: PortalAttendeeSource = {
      method: d?.method ?? null,
      mode: d?.mode ?? null,
      url: safeHttpUrl(d?.url ?? null),
      evidence: d?.evidence ?? null,
      provider: d?.provider ?? null,
      foundAt: d?.found_at ?? null,
    };
    attendeeSources.set(attendanceId, source);
    return source;
  },

  getEventCompanies: async (id: string, query: CompanyQuery = {}): Promise<CompaniesPage> => {
    if (FORCE_DEMO) return { total: 0, items: [] };
    const params = listParams(query, { role: query.role, tier: query.tier }, { has_domain: query.hasDomain });
    const res = await httpClient.get<{
      total: number;
      items: {
        id: string; name: string | null; domain: string | null; website: string | null;
        industry: string | null; country: string | null; role: string; tier: string | null;
      }[];
    }>(`/portal/events/${id}/companies`, { params });
    return {
      total: res.data?.total ?? 0,
      items: (res.data?.items ?? []).map(
        (c): PortalCompany => ({
          id: c.id,
          name: c.name,
          domain: c.domain,
          tier: c.tier,
          website: safeHttpUrl(c.website),
          industry: c.industry,
          country: c.country,
          role: c.role,
        }),
      ),
    };
  },

  getEventCompanyFacets: async (id: string): Promise<CompanyFacets> => {
    if (FORCE_DEMO) return { role: {}, tier: {} };
    const res = await httpClient.get<CompanyFacets>(`/portal/events/${id}/companies/facets`);
    return { role: res.data?.role ?? {}, tier: res.data?.tier ?? {} };
  },

  /** Whether this workspace has a sign in on file for the event's registration portal. */
  getPortalLogin: async (eventId: string): Promise<PortalLoginStatus> => {
    const res = await httpClient.get<PortalLoginStatus>(`/portal/events/${eventId}/credential`);
    return {
      exists: res.data?.exists ?? false,
      host: res.data?.host ?? null,
      url: res.data?.url ?? null,
    };
  },

  /** Saves the login; `resumed` counts the sourcing runs it unblocked. */
  savePortalLogin: async (eventId: string, login: PortalLoginInput): Promise<PortalLoginSaved> => {
    const res = await httpClient.post<PortalLoginSaved>(`/portal/events/${eventId}/credential`, login);
    return {
      exists: res.data?.exists ?? true,
      host: res.data?.host ?? null,
      url: res.data?.url ?? login.url,
      resumed: res.data?.resumed ?? 0,
    };
  },

  removePortalLogin: async (eventId: string): Promise<void> => {
    await httpClient.delete(`/portal/events/${eventId}/credential`);
  },

  markEventGoing: async (eventId: string, notes?: string, offer?: string): Promise<EventGoing> => {
    if (FORCE_DEMO) return demoApi.markEventGoing(eventId, notes, offer);
    const res = await httpClient.post<{ project_id: string; drafting?: boolean }>(
      `/portal/events/${eventId}/going`,
      { notes: notes?.trim() || null, offer: offer?.trim() || null },
    );
    const projectId = res.data?.project_id;
    if (!projectId) throw new Error('going response carried no project_id');
    return { projectId, drafting: res.data?.drafting === true };
  },

  getCampaigns: async (): Promise<PortalCampaign[]> => {
    if (FORCE_DEMO) return demoApi.getCampaigns();
    try {
      const res = await httpClient.get<ApiCampaignSummary[]>('/portal/campaigns', { params: { limit: 50 } });
      if (Array.isArray(res.data)) {
        // Refresh /portal/me on EVERY live path (deduped + cached) so getDataSource()
        // is authoritative at the call site — a stale 60s cache was silently gating
        // writes off on live workspaces (PR #569 review).
        const me = await fetchMe();
        // Empty is a REAL state for a live workspace (post-intake, pre-campaign) —
        // only fall back to demo when the workspace itself isn't reachable.
        if (res.data.length > 0 || me) {
          return res.data.map(mapCampaign);
        }
      }
    } catch { /* fall through */ }
    return demoApi.getCampaigns();
  },

  /** One campaign by id; null when it is not in the caller's workspace. Demo semantics as getCampaigns. */
  getCampaign: async (id: string): Promise<PortalCampaign | null> => {
    if (FORCE_DEMO) return demoApi.getCampaign(id);
    try {
      const res = await httpClient.get<ApiCampaignSummary>(`/portal/campaigns/${encodeURIComponent(id)}`);
      const campaign = mapCampaign(res.data);
      await fetchMe(); // keeps getDataSource() authoritative for the caller
      return campaign;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 422) return null;
      const demo = (await fetchMe()) ? null : await demoApi.getCampaign(id);
      if (demo) return demo;
      throw error;
    }
  },

  /** One tab page of the Campaigns list (SKY-564): the stage filter runs in SQL,
   * so tabs are never computed over a truncated window. Same demo semantics as
   * getCampaigns; the demo adapter filters and slices client-side. */
  getCampaignsPage: async (
    stage: CampaignStage, offset: number, search?: string,
  ): Promise<PortalCampaign[]> => {
    const term = search?.trim() ?? '';
    const needle = term.toLowerCase();
    const demo = async () =>
      (await demoApi.getCampaigns())
        .filter((c) => matchesCampaignStage(c, stage))
        .filter((c) => !needle || c.name.toLowerCase().includes(needle))
        .slice(offset, offset + CAMPAIGNS_PAGE_SIZE);
    if (FORCE_DEMO) return demo();
    try {
      const res = await httpClient.get<ApiCampaignSummary[]>('/portal/campaigns', {
        params: { limit: CAMPAIGNS_PAGE_SIZE, offset, stage, ...(term ? { q: term } : {}) },
      });
      if (Array.isArray(res.data)) {
        // A later page is live-only: an empty page legitimately means end of
        // list (exact page-size multiple), NEVER a demo fallback — that would
        // splice demo fixtures into a live list.
        if (offset > 0) return res.data.map(mapCampaign);
        const me = await fetchMe();
        if (res.data.length > 0 || me) return res.data.map(mapCampaign);
      }
    } catch (error) {
      console.error('portal campaigns page load failed:', error);
      // A later page must fail loudly: an empty demo slice would read as
      // "list complete" and silently hide the Show more button.
      if (offset > 0) throw error;
      // Demo fixtures may only render when the badge will actually say "demo":
      // a warm /portal/me cache means getDataSource() reports 'live', and
      // splicing fixtures under a live badge is a false claim.
      if (getDataSource() !== 'demo') throw error;
    }
    return demo();
  },

  getTodayQueue: async (projectId?: string): Promise<TodayQueuePage> => {
    if (FORCE_DEMO) return demoApi.getTodayQueue();
    try {
      const res = await httpClient.get<ApiTodayQueuePage>('/portal/today', {
        params: projectId ? { project_id: projectId } : undefined,
      });
      // An answer of "we could not read it" is still an answer: never swap it for demo
      // people, which would render five invented rows as a live queue.
      if (res.data?.state === 'being_verified') {
        return { state: 'being_verified', rows: [], truncated: false };
      }
      const wire = Array.isArray(res.data?.rows) ? res.data.rows : null;
      if (wire) {
        const me = await fetchMe(); // keep getDataSource() authoritative
        // Empty is a REAL state for a live workspace (nothing due before the
        // morning projection runs) — only fall back to demo when unreachable.
        if (wire.length > 0 || me) {
          const rows = wire.map((r) => ({
            id: r.id,
            campaignId: r.campaign_id,
            campaignName: r.campaign_name,
            channel: r.channel,
            person: r.person,
            title: r.title,
            company: r.company,
            nextStep: r.next_step,
            reason: r.reason,
            status: r.status,
            inCapacity: r.in_capacity,
            acknowledgedAt: r.acknowledged_at ?? null,
            holdable: r.holdable ?? true,
            simulated: r.simulated ?? false,
          }));
          return { state: 'fresh', rows, truncated: res.data?.truncated === true };
        }
      }
    } catch { /* fall through */ }
    return demoApi.getTodayQueue();
  },

  /** Hold/release one due send (SKY-414) — enforced at the provider; live-only (no demo mutation). */
  actOnTodaySend: async (sendId: string, action: 'hold' | 'release', reason?: string): Promise<{ id: string; status: string }> => {
    const res = await httpClient.patch<{ id: string; status: string }>(
      `/portal/today/${encodeURIComponent(sendId)}`,
      { action, reason: reason || null },
    );
    return res.data;
  },

  /** Campaign-scoped bulk action on today's queue (SKY-496): skip-today (hold_all,
   * partial-failure report) or client approve-all (acknowledge_all). Live-only. */
  actOnTodayCampaign: async (
    campaignId: string, action: 'hold_all' | 'acknowledge_all', reason?: string,
  ): Promise<TodayQueueBulkResult> => {
    const res = await httpClient.patch<TodayQueueBulkResult>(
      `/portal/today/campaigns/${encodeURIComponent(campaignId)}`,
      { action, reason: reason || null },
    );
    return res.data;
  },

  /** SKY-474 — provider-enforced campaign pause/resume; reason is required and audited. */
  setCampaignPaused: async (campaignId: string, paused: boolean, reason: string): Promise<{ id: string; status: 'active' | 'paused' }> => {
    const res = await httpClient.post<{ id: string; status: 'active' | 'paused' }>(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/${paused ? 'pause' : 'resume'}`,
      { reason },
    );
    return res.data;
  },

  getCampaignSequence: async (projectId: string): Promise<CampaignSequence> => {
    if (FORCE_DEMO) return { state: 'no_campaign', steps: [] };
    const res = await httpClient.get<{
      state: CampaignSequence['state'];
      steps: { index: number; subject: string | null; body: string | null; delay_days: number }[];
      plan_summary: string | null;
    }>(`/portal/campaigns/${projectId}/sequence`);
    return {
      state: res.data?.state ?? 'being_verified',
      steps: (res.data?.steps ?? []).map((st) => ({
        index: st.index,
        subject: st.subject,
        body: st.body,
        delayDays: st.delay_days ?? 0,
      })),
      planSummary: res.data?.plan_summary ?? null,
    };
  },

  getOutreachSequence: async (projectId: string): Promise<OutreachSequenceState> => {
    if (FORCE_DEMO) return demoApi.getOutreachSequence();
    const res = await httpClient.get<{
      engine: OutreachSequenceState['engine']; can_operate: boolean; sequence: OutreachSequenceWire | null; needs_attention?: string | null;
    }>(outreachUrl(projectId));
    return {
      engine: res.data.engine,
      canOperate: res.data.can_operate,
      sequence: res.data.sequence ? mapOutreachSequence(res.data.sequence) : null,
      needsAttention: res.data.needs_attention ?? null,
    };
  },

  /** Sends only the fields given; 422 carries the refusal, 409 a concurrent change. */
  updateOutreachSettings: async (projectId: string, input: OutreachSettingsInput): Promise<OutreachSettingsResult> => {
    if (FORCE_DEMO) return demoApi.updateOutreachSettings(input);
    const body = Object.fromEntries(Object.entries(input).map(([k, v]) => [OUTREACH_SETTINGS_WIRE[k as keyof OutreachSettingsInput], v]));
    const res = await httpClient.patch<{ sequence: OutreachSequenceWire; warnings?: string[] }>(outreachUrl(projectId, '/settings'), body);
    return { sequence: mapOutreachSequence(res.data.sequence), warnings: res.data.warnings ?? [] };
  },

  enrollApprovedLeads: async (projectId: string): Promise<OutreachEnrollResult> => {
    if (FORCE_DEMO) return demoApi.enrollApprovedLeads();
    const res = await httpClient.post<{ enrolled: number; skipped: { entity_id: string; reason: string }[] }>(outreachUrl(projectId, '/enroll'), {});
    return { enrolled: res.data.enrolled, skipped: res.data.skipped.map((s) => ({ entityId: s.entity_id, reason: s.reason })) };
  },

  getOutreachSenders: async (projectId: string): Promise<OutreachSender[]> => {
    if (FORCE_DEMO) return demoApi.getOutreachSenders();
    const res = await httpClient.get<{
      user_id: string; name: string | null; email: string | null; gmail_connected: boolean; linkedin_connected: boolean;
    }[]>(outreachUrl(projectId, '/senders'));
    return res.data.map((s) => ({
      userId: s.user_id, name: s.name, email: s.email, gmailConnected: s.gmail_connected, linkedinConnected: s.linkedin_connected,
    }));
  },

  createOutreachSequence: async (projectId: string): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const res = await httpClient.post<OutreachSequenceWire>(outreachUrl(projectId), {});
    return mapOutreachSequence(res.data);
  },

  addOutreachStep: async (projectId: string, step: OutreachStepInput): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const res = await httpClient.post<OutreachSequenceWire>(outreachUrl(projectId, '/steps'), {
      channel: step.channel, delay_days: step.delayDays, subject: step.subject, body: step.body,
      not_before: step.notBefore || null,
    });
    return mapOutreachSequence(res.data);
  },

  /** Rejects with a 409 carrying X-Upcoming-Sends when an unconfirmed edit changes upcoming sends. */
  updateOutreachStep: async (
    projectId: string, stepId: string, step: Omit<OutreachStepInput, 'channel'>, confirm = false,
  ): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const res = await httpClient.patch<OutreachSequenceWire>(outreachUrl(projectId, `/steps/${encodeURIComponent(stepId)}`), {
      delay_days: step.delayDays, subject: step.subject, body: step.body, not_before: step.notBefore || null, confirm,
    });
    return mapOutreachSequence(res.data);
  },

  decideOutreachStepChange: async (
    projectId: string, stepId: string, decision: 'approve' | 'discard', pendingAt: string,
  ): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const res = await httpClient.post<OutreachSequenceWire>(
      outreachUrl(projectId, `/steps/${encodeURIComponent(stepId)}/change`), { decision, pending_at: pendingAt },
    );
    return mapOutreachSequence(res.data);
  },

  /** `lastSendOn` null clears it; `noHardStop` is operator-only. */
  setOutreachCalendar: async (
    projectId: string, change: { lastSendOn?: string | null; noHardStop?: boolean },
  ): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const body: Record<string, unknown> = {};
    if (change.lastSendOn !== undefined) body.last_send_on = change.lastSendOn;
    if (change.noHardStop !== undefined) body.no_hard_stop = change.noHardStop;
    const res = await httpClient.patch<OutreachSequenceWire>(outreachUrl(projectId, '/calendar'), body);
    return mapOutreachSequence(res.data);
  },

  /** Operator-only: send to everyone held for already being in touch with the sender. */
  releaseOutreachContactHolds: async (projectId: string): Promise<number> => {
    if (FORCE_DEMO) return 0;
    const res = await httpClient.post<{ released: number }>(
      `/admin/outreach-sequences/${encodeURIComponent(projectId)}/release-contact-holds`, {},
    );
    return res.data.released;
  },

  deleteOutreachStep: async (projectId: string, stepId: string): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const res = await httpClient.delete<OutreachSequenceWire>(outreachUrl(projectId, `/steps/${encodeURIComponent(stepId)}`));
    return mapOutreachSequence(res.data);
  },

  reorderOutreachSteps: async (projectId: string, stepIds: string[]): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const res = await httpClient.post<OutreachSequenceWire>(outreachUrl(projectId, '/steps/reorder'), { step_ids: stepIds });
    return mapOutreachSequence(res.data);
  },

  setOutreachSequenceStatus: async (projectId: string, status: 'active' | 'paused'): Promise<OutreachSequence> => {
    if (FORCE_DEMO) return mockOutreachSequence;
    const res = await httpClient.patch<OutreachSequenceWire>(outreachUrl(projectId), { status });
    return mapOutreachSequence(res.data);
  },

  getSendingSettings: async (projectId: string): Promise<SendingSettings> => {
    if (FORCE_DEMO) return { state: 'no_campaign', window: null, dailyCap: null, ceiling: 0 };
    const res = await httpClient.get<{
      state: SendingSettings['state'];
      window: { days: number[]; from: string | null; to: string | null; timezone: string | null } | null;
      daily_cap: number | null;
      ceiling: number;
    }>(`/portal/campaigns/${projectId}/sending-settings`);
    return {
      state: res.data?.state ?? 'being_verified',
      window: res.data?.window ?? null,
      dailyCap: res.data?.daily_cap ?? null,
      ceiling: res.data?.ceiling ?? 0,
    };
  },

  getCampaignBrief: async (campaignId: string): Promise<CampaignBrief> => {
    const res = await httpClient.get<ApiCampaignBrief>(`/portal/campaigns/${encodeURIComponent(campaignId)}/brief`);
    return toCampaignBrief(res.data);
  },

  updateCampaignBrief: async (campaignId: string, field: BriefFieldName, value: string): Promise<CampaignBrief> => {
    const res = await httpClient.patch<ApiCampaignBrief>(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/brief`,
      { [`brief_${field}`]: value },
    );
    return toCampaignBrief(res.data);
  },

  approveCampaignBrief: async (campaignId: string, conferenceStartDate?: string): Promise<CampaignBrief> => {
    const res = await httpClient.post<ApiCampaignBrief>(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/brief/approve`,
      { conference_start_date: conferenceStartDate ?? null },
    );
    return toCampaignBrief(res.data);
  },

  setPeopleWanted: async (campaignId: string, peopleWanted: number): Promise<CampaignBrief> => {
    const res = await httpClient.patch<ApiCampaignBrief>(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/people-wanted`,
      { people_wanted: peopleWanted },
    );
    return toCampaignBrief(res.data);
  },

  getCreditEstimate: async (campaignId: string): Promise<CreditEstimate> => {
    const res = await httpClient.get<{
      credits: number;
      conference_credits: number;
      person_credits: number;
      likely_matches: number;
      people_minimum: number;
      available: number;
      held: number;
      exempt: boolean;
    }>(`/portal/campaigns/${encodeURIComponent(campaignId)}/credit-estimate`);
    return {
      credits: res.data.credits,
      conferenceCredits: res.data.conference_credits,
      personCredits: res.data.person_credits,
      likelyMatches: res.data.likely_matches,
      peopleMinimum: res.data.people_minimum,
      available: res.data.available,
      held: res.data.held,
      exempt: res.data.exempt,
    };
  },

  /** What the campaign's research found and charged; null while it runs or when exempt. */
  getResearchCredits: async (campaignId: string): Promise<ResearchCredits | null> => {
    const res = await httpClient.get<ResearchCredits | null>(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/research-credits`,
    );
    return res.data;
  },

  getCredits: async (): Promise<WorkspaceCredits> => {
    const res = await httpClient.get<WorkspaceCredits>('/portal/credits');
    return res.data;
  },

  getCreditUsage: async (page: number, pageSize: number): Promise<CreditUsagePage> => {
    const res = await httpClient.get<{ rows: ApiCreditUsageRow[]; total: number }>(
      '/portal/credits/usage',
      { params: { page, page_size: pageSize } },
    );
    return { rows: res.data.rows.map(toCreditUsageRow), total: res.data.total };
  },

  getTopUpPacks: async (): Promise<TopUpPack[]> => {
    const res = await httpClient.get<TopUpPack[]>('/portal/credits/top-up-packs');
    return res.data;
  },

  /** The Stripe checkout URL for a credit pack; it returns to portal Settings. */
  createTopUpCheckout: async (topUpPlanId: string): Promise<string> => {
    const res = await httpClient.post<{ checkout_session_url: string }>(
      '/portal/credits/top-up-checkout',
      { top_up_plan_id: topUpPlanId },
    );
    return res.data.checkout_session_url;
  },

  getPlan: async (): Promise<WorkspacePlan> => {
    const res = await httpClient.get<ApiWorkspacePlan>('/portal/plan');
    return toWorkspacePlan(res.data);
  },

  /** The Stripe checkout URL for the workspace's first plan; it returns to portal Settings. */
  createPlanCheckout: async (planId: string): Promise<string> => {
    const res = await httpClient.post<{ checkout_session_url: string }>('/portal/plan/checkout', { plan_id: planId });
    return res.data.checkout_session_url;
  },

  changePlan: async (planId: string): Promise<void> => {
    await httpClient.post('/portal/plan/change', { plan_id: planId });
  },

  cancelPlan: async (): Promise<void> => {
    await httpClient.post('/portal/plan/cancel');
  },

  /** What pays for the workspace's plan, or null without one. Owners only. */
  getPaymentMethod: async (): Promise<PaymentMethod | null> => {
    const res = await httpClient.get<{ payment_method: PaymentMethod | null }>('/billing/get-payment-method');
    return res.data.payment_method;
  },

  /** The Stripe page for changing the plan's payment method; it returns to portal Settings. */
  createPaymentMethodSession: async (): Promise<string> => {
    const res = await httpClient.post<{ checkout_session_url: string }>('/billing/update-payment-method');
    return res.data.checkout_session_url;
  },

  getCampaignLaunch: async (campaignId: string): Promise<LaunchSummary> => {
    const res = await httpClient.get<ApiLaunchSummary>(`/portal/campaigns/${encodeURIComponent(campaignId)}/launch`);
    return toLaunchSummary(res.data);
  },

  goLive: async (campaignId: string): Promise<LaunchSummary> => {
    const res = await httpClient.post<ApiLaunchSummary>(`/portal/campaigns/${encodeURIComponent(campaignId)}/go-live`, {});
    return toLaunchSummary(res.data);
  },

  getDinner: async (campaignId: string): Promise<DinnerBrief> => {
    const res = await httpClient.get<ApiDinnerBrief>(`/portal/campaigns/${encodeURIComponent(campaignId)}/dinner`);
    return toDinnerBrief(res.data);
  },

  /** Write the dinner fields sent; null clears one. */
  updateDinner: async (campaignId: string, update: DinnerUpdate): Promise<DinnerBrief> => {
    const res = await httpClient.patch<ApiDinnerBrief>(`/portal/campaigns/${encodeURIComponent(campaignId)}/dinner`, update);
    return toDinnerBrief(res.data);
  },

  /** Attach the dinner to its Luma event by its public or manage link. */
  linkDinnerEvent: async (campaignId: string, url: string): Promise<DinnerBrief> => {
    const res = await httpClient.put<ApiDinnerBrief>(`/portal/campaigns/${encodeURIComponent(campaignId)}/dinner/event`, { url });
    return toDinnerBrief(res.data);
  },

  getDinnerGuests: async (campaignId: string, offset = 0, limit = DINNER_GUEST_PAGE): Promise<DinnerGuestPage> => {
    const res = await httpClient.get<ApiGuestList>(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/guests`,
      { params: { offset, limit } },
    );
    const counts = res.data.counts ?? {};
    return {
      counts: Object.fromEntries(
        [...GUEST_STATUSES, 'walk_ins' as const].map((k) => [k, Number(counts[k] ?? 0)]),
      ) as GuestCounts,
      guests: (res.data.guests ?? []).map((g) => ({
        entityId: g.entity_id,
        name: g.name ?? null,
        company: g.company ?? null,
        status: GUEST_STATUSES.includes(g.status) ? g.status : 'invited',
        knownFrom: g.known_from ?? 'invited',
        rsvpNote: g.rsvp_note ?? null,
        suggestedEntityId: g.suggested_entity_id ?? null,
        suggestedName: g.suggested_name ?? null,
      })),
    };
  },

  /** The walk-in is the person we suggested: merge them into that person. */
  confirmGuestMatch: async (campaignId: string, entityId: string): Promise<void> => {
    await httpClient.post(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/guests/${encodeURIComponent(entityId)}/match`,
      {},
    );
  },

  /** The walk-in is someone new: keep them and drop the suggestion. */
  dismissGuestMatch: async (campaignId: string, entityId: string): Promise<void> => {
    await httpClient.delete(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/guests/${encodeURIComponent(entityId)}/match`,
    );
  },

  getCampaignPauseHistory: async (campaignId: string): Promise<CampaignPauseEvent[]> => {
    const res = await httpClient.get<{ action: 'paused' | 'resumed'; reason: string | null; occurred_at: string; actor_name: string | null; actor_is_client?: boolean }[]>(
      `/portal/campaigns/${encodeURIComponent(campaignId)}/pause-history`,
    );
    return (Array.isArray(res.data) ? res.data : []).map((e) => ({
      action: e.action,
      reason: e.reason,
      occurredAt: e.occurred_at,
      actorName: e.actor_name,
      actorIsClient: e.actor_is_client ?? false,
    }));
  },

  // Live-only: throws on total failure so surfaces degrade to no rows plus an
  // error state, never to fabricated demo replies rendered as real.
  getWorkspaceRepliesMeta: async (): Promise<{ rows: WorkspaceReply[]; complete: boolean }> => {
    if (FORCE_DEMO) return { rows: await demoWorkspaceReplies(), complete: true };
    return fetchWorkspaceRepliesPaged();
  },

  /** One thread's messages with full bodies. The list read sends previews, so the
   * message feed reads from here once a thread is opened. Demo rows already carry
   * their whole body, so there is nothing to fetch there. */
  getThreadReplies: async (threadId: string): Promise<WorkspaceReply[]> => {
    if (FORCE_DEMO) return [];
    const res = await httpClient.get<ApiReply[]>('/portal/thread-replies', {
      params: { thread_id: threadId },
    });
    if (!Array.isArray(res.data)) throw new Error('unexpected thread payload');
    return res.data.map((r) => ({
      ...mapReply(r),
      campaignId: r.campaign_id ?? '',
      campaignName: r.campaign_name ?? 'Campaign',
    }));
  },

  // Live-only ON PURPOSE: the ledger is a factual record, and silently swapping
  // in demo rows on failure would render a false history (SKY-498 / SKY-489
  // no-false-claims). Throws on failure — the page shows its load-error state.
  getSendHistory: async (query: SendHistoryQuery = {}): Promise<SendHistoryPage> => {
    if (FORCE_DEMO) return demoApi.getSendHistory(query);
    interface ApiSendRow {
      id: string; channel: 'email' | 'linkedin'; occurred_at: string | null; recipient: string | null;
      display_name: string | null; title: string | null; company: string | null;
      entity_id: string | null; project_id: string | null; campaign_name: string | null;
      step_index: number | null; next_step: string | null; mailbox_email: string | null;
      outcome: 'sent' | 'replied' | 'held' | 'skipped'; replied_at: string | null;
      reason: string | null; thread_id: string | null; subject: string | null; body: string | null;
    }
    const res = await httpClient.get<{
      as_of: string | null; state: 'fresh' | 'being_verified'; rows: ApiSendRow[]; next_before: string | null;
    }>('/portal/send-history', {
      params: {
        project_id: query.projectId, q: query.q || undefined, mailbox: query.mailbox || undefined,
        outcome: query.outcome === 'any' ? undefined : query.outcome,
        since: query.since, before: query.before, limit: query.limit,
      },
    });
    return {
      asOf: res.data.as_of,
      state: res.data.state === 'being_verified' ? 'being_verified' : 'fresh',
      nextBefore: res.data.next_before,
      rows: (res.data.rows ?? []).map((r) => ({
        id: r.id,
        // Anything that isn't the LinkedIn channel renders as email — same
        // vendor-neutral collapse as the replies mapper (SKY-470).
        channel: r.channel === 'linkedin' ? ('linkedin' as const) : ('email' as const),
        occurredAt: r.occurred_at,
        recipient: r.recipient,
        displayName: r.display_name,
        title: r.title,
        company: r.company,
        entityId: r.entity_id,
        projectId: r.project_id,
        campaignName: r.campaign_name,
        stepIndex: r.step_index,
        nextStep: r.next_step,
        mailboxEmail: r.mailbox_email,
        outcome: r.outcome,
        repliedAt: r.replied_at,
        reason: r.reason,
        threadId: r.thread_id,
        // Same body convention as replies (SKY-464): plain text for previews,
        // sanitized HTML for the expanded "what went out" view. LinkedIn is
        // never HTML — gate on channel, not on what the content looks like.
        subject: r.subject,
        body: r.body ? stripHtmlToTextWithBreaks(r.body) : null,
        bodyHtml:
          r.channel !== 'linkedin' && r.body && containsHtml(r.body)
            ? sanitizeEmailHtml(r.body)
            : null,
      })),
    };
  },

  /** SKY-477 campaign performance. A 200 from the stats endpoint IS live data —
   * trusted unconditionally (#648 review P2: requiring /portal/me too would
   * discard a valid live payload when /me blips). And a THROWN request on a live
   * workspace THROWS (#648 review P1): falling back to demo numbers would render
   * fabricated metrics unbadged, because the page badge follows getDataSource(),
   * which still says live. Demo numbers only ever serve a demo workspace. */
  getCampaignStatsHistory: async (campaignId: string, days = 30): Promise<CampaignStatsHistory> => {
    if (FORCE_DEMO) return demoApi.getCampaignStatsHistory(campaignId, days);
    try {
      const res = await httpClient.get<ApiCampaignStatsHistory>(
        `/portal/campaigns/${campaignId}/stats-history`,
        { params: { days } },
      );
      if (res.data && typeof res.data.state === 'string') {
        void fetchMe(); // keep getDataSource() warm; never gates a live 200
        return {
          state: res.data.state,
          funnel: res.data.funnel ?? null,
          sendingStartsOn: res.data.sending_starts_on ?? null,
          sendingStartsInDays: res.data.sending_starts_in_days ?? null,
          series: (res.data.series ?? []).map((d) => ({
            date: d.date,
            sent: d.sent ?? 0,
            opens: d.opens ?? 0,
            replies: d.replies ?? 0,
            clamped: d.clamped ?? false,
            spannedDays: d.spanned_days ?? null,
            partial: d.partial ?? false,
          })),
          days: res.data.days ?? days,
        };
      }
    } catch (err) {
      if (getDataSource() !== 'demo') throw err;
    }
    // Malformed 200s land here too — same rule as thrown requests: a live
    // workspace never silently falls back to demo numbers.
    if (getDataSource() !== 'demo') throw new Error('stats-history returned an unexpected payload');
    return demoApi.getCampaignStatsHistory(campaignId, days);
  },

  /** SKY-536 sequence projection — FROZEN contract; endpoint follows as its
   * own PR. null return = wired-but-empty is impossible by contract, so the
   * component treats null/404 alike as the not-wired state. */
  getLinkedInSequence: async (campaignId: string): Promise<import('./types').LinkedInSequence | null> => {
    if (FORCE_DEMO) return demoApi.getLinkedInSequence(campaignId);
    const res = await httpClient.get<{
      account_id: string | null; captured_at: string | null;
      steps: { step: number; label: string; step_delay_days: number | null; inferred: boolean; variants: { key: string; text: string | null }[] }[];
    }>(`/portal/campaigns/${campaignId}/linkedin-sequence`);
    const d = res.data;
    if (!d || d.account_id === null) return null;
    return {
      accountId: d.account_id,
      capturedAt: d.captured_at,
      steps: (d.steps ?? []).map((st) => ({
        step: st.step, label: st.label, stepDelayDays: st.step_delay_days,
        inferred: !!st.inferred, variants: st.variants ?? [],
      })),
    };
  },

  /** SKY-535 LinkedIn report — per-day deltas + tallies. Demo serves the
   * fixture-mirrored mock; live failures surface as errors, never demo data. */
  getLinkedInReport: async (campaignId: string, days = 30): Promise<import('./types').LinkedInReport> => {
    if (FORCE_DEMO) return demoApi.getLinkedInReport(campaignId, days);
    const res = await httpClient.get<{
      state: string; account_id: string | null;
      funnel: { invited: number; accepted: number; messaged: number; replied: number } | null;
      series: { date: string; invited: number; accepted: number; messaged: number; replied: number; clamped: boolean; spanned_days: number | null; partial: boolean }[];
      days: number; synced_at: string | null;
    }>(`/portal/campaigns/${campaignId}/linkedin-report?days=${days}`);
    const d = res.data;
    return {
      state: (d.state === 'fresh' || d.state === 'being_verified' || d.state === 'no_campaign' ? d.state : 'being_verified'),
      accountId: d.account_id,
      funnel: d.funnel,
      series: (d.series ?? []).map((p) => ({
        date: p.date, invited: p.invited, accepted: p.accepted, messaged: p.messaged, replied: p.replied,
        clamped: !!p.clamped, spannedDays: p.spanned_days, partial: !!p.partial,
      })),
      days: d.days,
      syncedAt: d.synced_at,
    };
  },

  /** SKY-533 roster page (LinkedIn audience). Live-only shape; demo serves the
   * fixture-mirrored mock. Fail-open to demo ONLY in forced-demo — a live
   * failure surfaces as an error state, never fabricated leads. */
  getLinkedInLeads: async (campaignId: string, offset = 0): Promise<import('./types').LinkedInLeadsPage> => {
    if (FORCE_DEMO) return demoApi.getLinkedInLeads(campaignId, offset);
    const res = await httpClient.get<{
      account_id: string | null; total: number; limit: number; offset: number;
      leads: {
        lead_id: string; linkedin_url: string | null; name: string | null; company: string | null;
        title: string | null; stage: string; personalization_line: string | null; variant: string | null;
        entity_id: string | null; last_event_type: string | null; last_step: number | null; last_at: string | null;
      }[];
    }>(`/portal/campaigns/${campaignId}/linkedin-leads?limit=100&offset=${offset}`);
    const d = res.data;
    return {
      accountId: d.account_id,
      total: d.total,
      limit: d.limit,
      offset: d.offset,
      leads: (d.leads ?? []).map((l) => ({
        leadId: l.lead_id,
        linkedinUrl: l.linkedin_url,
        name: l.name,
        company: l.company,
        title: l.title,
        stage: l.stage,
        personalizationLine: l.personalization_line,
        variant: l.variant,
        entityId: l.entity_id,
        lastEventType: (l.last_event_type ?? null) as import('./types').LinkedInRosterLead['lastEventType'],
        lastStep: l.last_step,
        lastAt: l.last_at,
      })),
    };
  },

  /** SKY-534 drill: one lead's LinkedIn timeline (endpoint shipped with SKY-522/#685). */
  getLinkedInLeadTimeline: async (leadId: string): Promise<import('./types').LinkedInLeadTimeline> => {
    if (FORCE_DEMO) return demoApi.getLinkedInLeadTimeline(leadId);
    const res = await httpClient.get<Record<string, unknown> & { events: Record<string, unknown>[] }>(
      `/portal/linkedin-leads/${leadId}/timeline`,
    );
    const d = res.data as {
      lead_id: string; account_id: string; linkedin_url: string | null; name: string | null;
      company: string | null; title: string | null; stage: string; entity_id: string | null;
      personalization_line: string | null; variant: string | null; segment: string | null;
      events: { type: string; at: string; step: number | null; text: string | null; template_key: string | null; template_inferred: boolean }[];
    };
    return {
      leadId: d.lead_id, accountId: d.account_id, linkedinUrl: d.linkedin_url, name: d.name,
      company: d.company, title: d.title, stage: d.stage, entityId: d.entity_id,
      personalizationLine: d.personalization_line, variant: d.variant, segment: d.segment,
      events: (d.events ?? []).map((e) => ({
        type: e.type, at: e.at, step: e.step, text: e.text,
        templateKey: e.template_key, templateInferred: !!e.template_inferred,
      })),
    };
  },

  getCampaignLeads: async (campaignId: string): Promise<CampaignLead[]> => {
    if (FORCE_DEMO) return demoApi.getCampaignLeads(campaignId);
    try {
      const res = await httpClient.get<
        { email: string; name: string | null; company: string | null; title: string | null; entity_id: string | null }[]
      >(`/portal/campaigns/${campaignId}/leads`);
      if (Array.isArray(res.data)) {
        const me = await fetchMe(); // keep getDataSource() authoritative (PR #569 review)
        // Empty is a REAL state for a reachable workspace (sync hasn't run / unstamped)
        if (res.data.length > 0 || me) {
          return res.data.map((l) => ({
            email: l.email,
            name: l.name ?? null,
            company: l.company ?? null,
            title: l.title ?? null,
            entityId: l.entity_id ?? null,
          }));
        }
      }
    } catch (error) {
      // Live workspaces surface the failure; only the demo experience falls back.
      if (getDataSource() !== 'demo') throw error;
    }
    return demoApi.getCampaignLeads(campaignId);
  },

  /** SKY-488 copy review. THROWS on failure — no demo fallback: fabricated copy
   * on a review-and-approve surface would invite approving text that doesn't exist.
   * Empty array is the real "no copy uploaded yet" state. */
  getCampaignCopy: async (campaignId: string): Promise<CopyVariant[]> => {
    if (FORCE_DEMO) return [];
    const res = await httpClient.get<
      {
        id: string; track: string; version: number; subject: string | null; body: string;
        created_at: string; decision: string | null; decision_reason: string | null; decided_at: string | null;
        rule_check: { status: string; flags: { rule: string; note: string }[] } | null;
      }[]
    >(`/portal/campaigns/${campaignId}/copy`);
    // A malformed envelope must NOT read as the meaningful "no copy yet" empty state.
    if (!Array.isArray(res.data)) throw new Error('Malformed copy response');
    return res.data.map((v) => ({
      id: v.id,
      track: v.track,
      version: v.version,
      subject: v.subject ?? null,
      body: v.body,
      createdAt: v.created_at,
      decision: v.decision === 'approve' || v.decision === 'reject' ? v.decision : null,
      decisionReason: v.decision_reason ?? null,
      decidedAt: v.decided_at ?? null,
      ruleCheck:
        v.rule_check && (v.rule_check.status === 'clean' || v.rule_check.status === 'flagged')
          ? { status: v.rule_check.status, flags: v.rule_check.flags ?? [] }
          : null,
    }));
  },

  /** SKY-488 rules of engagement. All THROW on failure — an empty rules panel
   * means "no rules", so a swallowed error would fabricate that state. */
  getRules: async (): Promise<EngagementRule[]> => {
    if (FORCE_DEMO) return [];
    const res = await httpClient.get<{ id: string; rule: string; created_at: string | null }[]>('/portal/rules');
    if (!Array.isArray(res.data)) throw new Error('Malformed rules response');
    return res.data.map((r) => ({ id: r.id, rule: r.rule, createdAt: r.created_at ?? null }));
  },

  createRule: async (rule: string): Promise<EngagementRule> => {
    const res = await httpClient.post<{ id: string; rule: string; created_at: string | null }>('/portal/rules', { rule });
    return { id: res.data.id, rule: res.data.rule, createdAt: res.data.created_at ?? null };
  },

  deleteRule: async (ruleId: string): Promise<void> => {
    await httpClient.delete(`/portal/rules/${ruleId}`);
  },

  /** SKY-488: approve or request changes on one copy version. THROWS on failure —
   * an approval that didn't land must never render as approved. */
  decideCampaignCopy: async (
    variantId: string, decision: 'approve' | 'reject', reason?: string,
  ): Promise<void> => {
    await httpClient.post(`/portal/copy/${variantId}/decision`, { decision, reason: reason ?? null });
  },

  /** What the company does or sells, as the copy for its campaigns describes it. */
  getCompanyDescription: async (): Promise<CompanyDescription> => {
    if (FORCE_DEMO) return { companyDescription: 'We run outbound for early-stage founders.' };
    const res = await httpClient.get<{ company_description?: unknown }>(
      '/portal/company-description',
    );
    return parseCompanyDescription(res.data);
  },

  /** Save what the company does. New campaigns describe it this way. */
  saveCompanyDescription: async (description: string): Promise<CompanyDescription> => {
    if (FORCE_DEMO) return { companyDescription: description };
    const res = await httpClient.put<{ company_description?: unknown }>(
      '/portal/company-description', { company_description: description },
    );
    return parseCompanyDescription(res.data);
  },

  /** The lines every email ends with: what is stored, else the default we would use. */
  getSignOff: async (projectId?: string): Promise<SignOff> => {
    if (FORCE_DEMO) return { signOff: 'Best,\nMaya' };
    const params = projectId ? { project_id: projectId } : undefined;
    const res = await httpClient.get<{ sign_off?: unknown }>('/portal/sign-off', { params });
    return parseSignOff(res.data);
  },

  /** Save the sign-off for every email of the workspace; copy approvals are untouched. */
  saveSignOff: async (signOff: string): Promise<SignOff> => {
    if (FORCE_DEMO) return { signOff };
    const res = await httpClient.put<{ sign_off?: unknown }>(
      '/portal/sign-off', { sign_off: signOff },
    );
    return parseSignOff(res.data);
  },

  /** Per-person copy review. All THROW on failure — a review surface must never
   * fabricate an empty or approved state. */
  getLeadCopySummary: async (campaignId: string): Promise<LeadCopyTrackSummary[]> => {
    if (FORCE_DEMO) return [];
    const res = await httpClient.get<
      { track: string; leads: number; approved: number; rejected: number; awaiting: number }[]
    >(`/portal/campaigns/${campaignId}/lead-copy`);
    if (!Array.isArray(res.data)) throw new Error('Malformed lead copy response');
    return res.data.map((t) => {
      const counts = [t.leads, t.approved, t.rejected, t.awaiting];
      if (!t.track || counts.some((n) => !Number.isFinite(n))) throw new Error('Malformed lead copy response');
      return { track: t.track, leads: t.leads, approved: t.approved, rejected: t.rejected, awaiting: t.awaiting };
    });
  },

  /** A page of people by name with their latest message per track, narrowed to one standing
   * when `status` is given; a full page means more may exist. `asOf` is the server moment the
   * page was read, handed back on approve-all; `counts` covers everyone and comes with the
   * first page only (zeros after that). */
  getLeadCopyPeople: async (
    campaignId: string, offset: number, limit: number, status?: LeadCopyStatus,
  ): Promise<LeadCopyPeoplePage> => {
    const none: LeadCopyStatusCounts = { pending: 0, approved: 0, changes_requested: 0 };
    if (FORCE_DEMO) return { asOf: new Date().toISOString(), counts: none, people: [] };
    const res = await httpClient.get<{
      as_of: string;
      counts: Partial<LeadCopyStatusCounts>;
      people: {
        entity_id: string; name: string; company: string | null;
        messages: {
          id: string; track: string; version: number; subject: string | null; body: string;
          flag: string | null; decision: string | null; decision_reason: string | null;
        }[];
      }[];
    }>(`/portal/campaigns/${campaignId}/lead-copy/people`, { params: { offset, limit, ...(status ? { status } : {}) } });
    if (!Array.isArray(res.data?.people) || typeof res.data.as_of !== 'string') {
      throw new Error('Malformed lead copy people response');
    }
    const people: LeadCopyPerson[] = res.data.people.map((p) => ({
      entityId: p.entity_id,
      name: p.name,
      company: p.company ?? null,
      messages: p.messages.map((m) => ({
        id: m.id,
        track: m.track,
        version: m.version,
        subject: m.subject ?? null,
        body: m.body,
        flag: m.flag === 'no_facts' || m.flag === 'check_failed' ? m.flag : null,
        decision: m.decision === 'approve' || m.decision === 'reject' ? m.decision : null,
        decisionReason: m.decision_reason ?? null,
      })),
    }));
    return { asOf: res.data.as_of, counts: { ...none, ...res.data.counts }, people };
  },

  /** Approve or request changes on some of one person's messages, pinned to the versions on screen. */
  decideLeadCopyPerson: async (
    campaignId: string, entityId: string, decision: 'approve' | 'reject', variantIds: string[], reason?: string,
  ): Promise<void> => {
    await httpClient.post(`/portal/campaigns/${campaignId}/lead-copy/people/${entityId}/decision`, {
      decision, reason: reason ?? null, copy_variant_ids: variantIds,
    });
  },

  /** Save the client's own text as the next, approved version of a message; `problems` are the copy check's warnings. */
  editLeadCopyMessage: async (
    campaignId: string, entityId: string, variantId: string, subject: string | null, body: string,
  ): Promise<{ id: string; version: number; problems: string[] }> => {
    const res = await httpClient.post<{ id: string; version: number; problems: string[] }>(
      `/portal/campaigns/${campaignId}/lead-copy/people/${entityId}/messages`,
      { copy_variant_id: variantId, subject, body },
    );
    if (typeof res.data?.id !== 'string' || typeof res.data?.version !== 'number') {
      throw new Error('Malformed lead copy edit response');
    }
    return { id: res.data.id, version: res.data.version, problems: res.data.problems ?? [] };
  },

  /** Approve every person's latest message written up to `asOf` (the moment the page was
   * read): what was approved now, what already stood, and what is newer than the page. */
  approveAllLeadCopy: async (
    campaignId: string, asOf: string,
  ): Promise<{ approved: number; alreadyApproved: number; newer: number }> => {
    const res = await httpClient.post<{ approved: number; already_approved: number; newer: number }>(
      `/portal/campaigns/${campaignId}/lead-copy/approve-all`, { as_of: asOf },
    );
    const { approved, already_approved: alreadyApproved, newer } = res.data ?? {};
    if (typeof approved !== 'number' || typeof alreadyApproved !== 'number' || typeof newer !== 'number') {
      throw new Error('Malformed approve-all response');
    }
    return { approved, alreadyApproved, newer };
  },

  // opts.demoFallback === false: throw on failure / return [] on an empty live
  // workspace — Home's gone-quiet rows must never surface fabricated demo people.
  getTam: async (filters?: TamFilters, offset = 0, opts?: { demoFallback?: boolean }) => {
    if (FORCE_DEMO) return demoApi.getTam(filters);
    try {
      // Filters ride to the DB (SKY-456) — one fetched page is not the population.
      const res = await httpClient.get<ApiTamPerson[]>('/portal/tam', {
        params: {
          limit: TAM_PAGE_SIZE,
          offset,
          ...(filters?.q?.trim() ? { q: filters.q.trim() } : {}),
          ...(filters?.status && filters.status !== 'all' ? { status: filters.status } : {}),
          ...(filters?.replied ? { replied: true } : {}),
          ...(filters?.stale ? { stale: true } : {}),
          ...(filters?.listId ? { list_id: filters.listId } : {}),
          ...(filters?.tier ? { tier: filters.tier } : {}),
          ...(filters?.sort ? { sort: filters.sort, dir: filters.dir ?? 'asc' } : {}),
          ...(filters?.withChannels ? { channels: true } : {}),
        },
      });
      if (Array.isArray(res.data)) {
        const me = await fetchMe(); // keep getDataSource() authoritative (PR #569 review)
        // Empty is a REAL state for a reachable workspace (pre-upload)
        if (res.data.length > 0 || me || offset > 0) {
          return res.data.map((t) => ({
            entityId: t.entity_id,
            name: t.name,
            title: t.title,
            company: t.company,
            companyEntityId: t.company_entity_id ?? null,
            email: t.email ?? null,
            // Scheme allow-list (review P1): identifier values are CSV/enrichment
            // influenced — a javascript: href must die at the mapping boundary.
            linkedinUrl: safeHttpUrl(t.linkedin_url),
            // Honest null: an unassigned tier renders as unset, never a default T2.
            tier: (t.tier as 'T1' | 'T2' | 'T3' | 'skip' | null),
            suppressed: t.suppressed,
            lastSignalAt: t.last_signal_at,
            campaignIds: [], // project↔list association is the W3 gap — none on the wire yet
            replied: t.replied ?? false,
            addedAt: t.added_at ?? null,
            disposition: (t.disposition as LeadDisposition | null) ?? null,
            matchReason: t.match_reason ?? null,
            provisional: t.provisional ?? false,
            lastTouchAt: t.last_touch_at ?? null,
            stale: t.stale ?? false,
            channels: t.channels ?? [],
          }));
        }
      }
    } catch (error) {
      // Surface offset-page failures to Load-more's toast instead of silently
      // ending the list; first page keeps the demo fallback below.
      if (offset > 0 || opts?.demoFallback === false) throw error;
    }
    // Offset pages NEVER fall back to demo — appending demo rows to a live
    // list would corrupt it. First page keeps the existing fallback.
    if (opts?.demoFallback === false) return [];
    return offset > 0 ? [] : demoApi.getTam(filters);
  },

  // Person-page header (SKY-513). null = 404 (not in this workspace) — the
  // page renders an honest not-found; other failures throw to its error state.
  getPersonDetail: async (entityId: string): Promise<PersonDetail | null> => {
    if (FORCE_DEMO) return demoApi.getPersonDetail(entityId);
    try {
      const res = await httpClient.get<{
        entity_id: string; name: string; title: string | null; company: string | null;
        company_entity_id: string | null; location: string | null; tier: string | null;
        suppressed: boolean; replied: boolean; provisional: boolean; added_at: string | null;
        email: string | null; linkedin_url: string | null;
        colleagues: { entity_id: string; name: string; title: string | null }[];
      }>(`/portal/tam/${entityId}`);
      const d = res.data;
      return {
        entityId: d.entity_id,
        name: d.name,
        title: d.title,
        company: d.company,
        companyEntityId: d.company_entity_id,
        location: d.location,
        tier: (d.tier as PersonDetail['tier']) ?? null,
        suppressed: d.suppressed,
        replied: d.replied,
        provisional: d.provisional,
        addedAt: d.added_at,
        email: d.email,
        linkedinUrl: safeHttpUrl(d.linkedin_url),
        colleagues: (d.colleagues ?? []).map((c) => ({
          entityId: c.entity_id,
          name: c.name,
          title: c.title,
        })),
      };
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
      // Demo-fallback parity with getTam (review P2): when live rows fell back
      // to demo data, their ids (e_1…) 422 on the UUID-typed live route — serve
      // the matching demo page instead. A transient live failure for a REAL id
      // finds nothing in the demo set and rethrows, keeping the failed state
      // honest rather than claiming "not in your list".
      const demo = await demoApi.getPersonDetail(entityId);
      if (demo) return demo;
      throw error;
    }
  },

  // null = counts unavailable (demo mode or a failed read) — the pills render
  // without numbers rather than with fabricated zeros (SKY-513).
  getTamCounts: async (filters?: TamFilters): Promise<TamCounts | null> => {
    if (FORCE_DEMO) return null;
    try {
      const res = await httpClient.get<TamCounts>('/portal/tam/counts', {
        params: {
          ...(filters?.q?.trim() ? { q: filters.q.trim() } : {}),
          ...(filters?.status && filters.status !== 'all' ? { status: filters.status } : {}),
          ...(filters?.replied ? { replied: true } : {}),
          ...(filters?.stale ? { stale: true } : {}),
          ...(filters?.listId ? { list_id: filters.listId } : {}),
          ...(filters?.tier ? { tier: filters.tier } : {}),
        },
      });
      return res.data ?? null;
    } catch (error) {
      console.error('TAM counts load failed:', error);
      return null;
    }
  },

  // opts.demoFallback === false: throw on failure instead of resolving with demo
  // data — the waitlist gate must never show fabricated build progress.
  getLeadLists: async (opts?: { demoFallback?: boolean; projectId?: string }): Promise<LeadList[]> => {
    if (FORCE_DEMO) {
      const lists = await demoApi.getLeadLists();
      return opts?.projectId ? lists.filter((l) => l.gtmProjectId === opts.projectId) : lists;
    }
    try {
      const res = await httpClient.get<
        {
          id: string; name: string; kind: string; gtm_project_id: string | null; member_count: number;
          kept_count: number; cut_count: number; undecided_count: number; approved_at: string | null;
          created_at: string | null;
        }[]
      >('/portal/lead-lists', { params: opts?.projectId ? { project_id: opts.projectId } : {} });
      if (Array.isArray(res.data)) {
        const me = await fetchMe(); // keep getDataSource() authoritative
        // Empty is a REAL state for a reachable workspace (no batches yet)
        if (res.data.length > 0 || me) {
          return res.data.map((l) => ({
            id: l.id,
            name: l.name,
            kind: l.kind,
            createdAt: l.created_at ?? null,
            gtmProjectId: l.gtm_project_id ?? null,
            memberCount: l.member_count ?? 0,
            keptCount: l.kept_count ?? 0,
            cutCount: l.cut_count ?? 0,
            undecidedCount: l.undecided_count ?? 0,
            approvedAt: l.approved_at ?? null,
          }));
        }
      }
    } catch (error) {
      if (opts?.demoFallback === false) throw error;
    }
    if (opts?.demoFallback === false) return []; // unreachable-workspace 200 case: no lists, not demo lists
    return demoApi.getLeadLists();
  },

  /** Both throw on failure. */
  setLeadDisposition: async (
    listId: string, entityId: string, disposition: LeadDisposition, reason?: string,
  ): Promise<void> => {
    await httpClient.patch(
      `/portal/lead-lists/${listId}/members/${entityId}`,
      { disposition, reason: reason ?? null },
    );
  },

  approveLeadList: async (listId: string): Promise<string> => {
    const res = await httpClient.post<{ approved_at: string }>(`/portal/lead-lists/${listId}/approve`, {});
    return res.data.approved_at;
  },

  /** SKY-467 resolve-first: email → workspace entity id, or null (honest unresolved).
   * THROWS on request failure — "couldn't look up" must never render as the factual
   * claim "this person has no record" (the panel shows its error state instead). */
  resolveLeadEntity: async (email: string): Promise<string | null> => {
    if (FORCE_DEMO) return demoApi.resolveLeadEntity(email);
    const res = await httpClient.get<{ entity_id: string | null }>(
      `/portal/leads/${encodeURIComponent(email)}/history`,
    );
    return res.data?.entity_id ?? null;
  },

  /** SKY-475 person-panel block. THROWS on failure — an unreadable derivation must
   * never render as the factual claim "never touched" (the panel omits the block). */
  getPersonLastTouch: async (entityId: string): Promise<PersonLastTouch> => {
    if (FORCE_DEMO) return demoApi.getPersonLastTouch(entityId);
    const res = await httpClient.get<{
      last_touch_at: string | null; stale: boolean;
      next_send_due_on: string | null; next_send_step: string | null; stopped_on_reply: boolean;
    }>(`/portal/tam/${entityId}/last-touch`);
    return {
      lastTouchAt: res.data.last_touch_at ?? null,
      stale: res.data.stale ?? false,
      nextSendDueOn: res.data.next_send_due_on ?? null,
      nextSendStep: res.data.next_send_step ?? null,
      stoppedOnReply: res.data.stopped_on_reply ?? false,
    };
  },

  /** SKY-478 Accounts list: company rollups, touched first, server-side search.
   * Paged — callers pass offset and detect "more" by a full page. */
  getAccounts: async (
    q?: string,
    offset = 0,
    sort?: 'name' | 'last_touch',
    dir?: 'asc' | 'desc',
  ): Promise<AccountsPage> => {
    if (FORCE_DEMO) return demoApi.getAccounts(q);
    const res = await httpClient.get<{
      total: number;
      accounts: {
        entity_id: string; name: string; domain: string | null;
        people_count: number; replying_count: number; last_touch_at: string | null;
      }[];
    }>('/portal/accounts', {
      params: {
        limit: ACCOUNTS_PAGE_SIZE,
        offset,
        ...(q?.trim() ? { q: q.trim() } : {}),
        ...(sort ? { sort, dir: dir ?? 'asc' } : {}),
      },
    });
    return {
      total: res.data?.total ?? 0,
      accounts: (res.data?.accounts ?? []).map((a) => ({
        entityId: a.entity_id,
        name: a.name,
        domain: a.domain ?? null,
        peopleCount: a.people_count ?? 0,
        replyingCount: a.replying_count ?? 0,
        lastTouchAt: a.last_touch_at ?? null,
      })),
    };
  },

  /** SKY-478 account page: identity + brief claims. Claims arrive pre-filtered —
   * anything unsourced or internally-sourced never left the backend. */
  getAccount: async (entityId: string): Promise<PortalAccountDetail> => {
    if (FORCE_DEMO) return demoApi.getAccount(entityId);
    const res = await httpClient.get<{
      entity_id: string; name: string; domain: string | null; people_count: number;
      replying_count: number; stage: AccountStage;
      last_touch_at: string | null; has_brief: boolean; brief_updated_at: string | null;
      claims: { section: string; text: string; sources: { label: string; date: string }[] }[];
    }>(`/portal/accounts/${entityId}`);
    const a = res.data;
    return {
      entityId: a.entity_id,
      name: a.name,
      domain: a.domain ?? null,
      peopleCount: a.people_count ?? 0,
      replyingCount: a.replying_count ?? 0,
      stage: a.stage ?? 'mapped',
      lastTouchAt: a.last_touch_at ?? null,
      hasBrief: a.has_brief ?? false,
      briefUpdatedAt: a.brief_updated_at ?? null,
      claims: a.claims ?? [],
    };
  },

  /** Corrections the client filed on this account (client_correction feed rows). */
  getAccountCorrections: async (entityId: string): Promise<AccountCorrection[]> => {
    if (FORCE_DEMO) return demoApi.getAccountCorrections(entityId);
    const res = await httpClient.get<{
      items: {
        activity_type: string; occurred_at: string;
        data: { claim_excerpt?: string; correction_text?: string } | null;
      }[];
      next_cursor: string | null;
    }>(`/portal/accounts/${entityId}/timeline`, {
      // Server-side type filter: on a busy account, corrections older than the
      // most recent activities must still reach the brief overlay.
      params: { limit: 100, type: 'client_correction' },
    });
    // Server-side type filter is the contract; the only client-side guard is
    // the null-data check.
    return (res.data?.items ?? [])
      .filter((i) => i.data)
      .map((i) => ({
        claimExcerpt: i.data?.claim_excerpt ?? '',
        correctionText: i.data?.correction_text ?? '',
        occurredAt: i.occurred_at,
      }));
  },

  /** SKY-478 client-safe activity feed for a person or account. The backend
   * filters activity types and maps vendor sources to neutral labels; the
   * label rides in the TimelineActivity source_type slot for rendering.
   * nextCursor is the explicit "more available" signal: a read-capped page can
   * be shorter than FEED_PAGE_SIZE while older items remain, so callers must
   * page on the cursor, never on page length. */
  getEntityActivityFeed: async (
    entityId: string,
    before?: string,
  ): Promise<{ items: TimelineActivity[]; nextCursor: string | null }> => {
    if (FORCE_DEMO) return { items: await demoApi.getEntityActivityFeed(entityId), nextCursor: null };
    const res = await httpClient.get<{
      items: {
        id: string; activity_type: string; source_label: string; occurred_at: string;
        actor_name: string | null; has_message?: boolean;
      }[];
      next_cursor: string | null;
    }>(`/portal/accounts/${entityId}/timeline`, {
      params: { limit: FEED_PAGE_SIZE, ...(before ? { before } : {}) },
    });
    return {
      items: (res.data?.items ?? []).map((item) => ({
        id: item.id,
        activity_type: item.activity_type,
        activity_category: null,
        source_type: item.source_label,
        occurred_at: item.occurred_at,
        label: feedSentence(item.activity_type, item.actor_name),
        has_message: item.has_message === true,
      })),
      nextCursor: res.data?.next_cursor ?? null,
    };
  },

  /** SKY-527: the actual message behind one feed row, fetched on click, never
   * eagerly. 404 = not found or "not available" (source content gone); callers
   * render both as their own state. bodyHtml is sanitized here, same
   * convention as the reply and send-history mappers. */
  getTimelineMessage: async (activityId: string): Promise<TimelineMessage> => {
    const res = await httpClient.get<{
      subject: string | null; body: string | null; body_html: string | null;
      body_truncated?: boolean;
      direction: 'inbound' | 'outbound'; counterpart: string | null;
      occurred_at: string; thread_id: string | null; campaign_id: string | null;
    }>(`/portal/timeline/${activityId}/message`);
    const m = res.data;
    return {
      subject: m?.subject ?? null,
      body: m?.body ?? null,
      bodyHtml: m?.body_html ? sanitizeEmailHtml(m.body_html) : null,
      bodyTruncated: m?.body_truncated === true,
      direction: m?.direction === 'outbound' ? 'outbound' : 'inbound',
      counterpart: m?.counterpart ?? null,
      occurredAt: m?.occurred_at ?? '',
      threadId: m?.thread_id ?? null,
      campaignId: m?.campaign_id ?? null,
    };
  },

  // ---- write operations (no demo fallback: failures surface to the UI for toasts) ----

  saveReplyDraft: async (replyId: string, body: string): Promise<{ status: string }> => {
    const res = await httpClient.post<{ status: string }>(`/portal/replies/${replyId}/draft`, { body });
    return { status: res.data?.status ?? 'draft' };
  },

  /** Direct send through the campaign mailbox (the operator gate is gone).
   * Throws on failure; a 409 means duplicate or pacing, message in detail. */
  sendReply: async (
    replyId: string,
    body: string,
    cc: string[] = [],
    bcc: string[] = [],
  ): Promise<void> => {
    // cc/bcc omitted when empty so a plain reply keeps the original payload shape.
    await httpClient.post(`/portal/replies/${replyId}/send`, {
      body,
      ...(cc.length ? { cc } : {}),
      ...(bcc.length ? { bcc } : {}),
    });
  },

  /** SKY-452 tone chips. Claude runs on the runner queue, never in-process, so
   * this files a run and polls it. Returns the rewritten draft. One skill per
   * channel (not one skill branching on channel) so the channel's tone rules are
   * a dispatch decision, never something the model has to notice and apply. */
  rewriteDraft: async (
    replyId: string,
    tone: ReplyTone,
    draft: string,
    projectId: string | null,
    channel: 'email' | 'linkedin',
  ): Promise<string> => {
    const started = await httpClient.post<{ run_id: string }>('/runs', {
      task: channel === 'email' ? 'reply-rewrite-email' : 'reply-rewrite-linkedin',
      params: { reply_id: replyId, tone, draft },
      ...(projectId ? { gtm_project_id: projectId } : {}),
    });
    const runId = started.data?.run_id;
    if (!runId) throw new Error('rewrite run was not queued');
    const run = await portalApi.waitForRun(`/runs/${runId}`, REWRITE_TIMEOUT_MS, {
      pollMs: REWRITE_POLL_MS,
    });
    if (!run) throw new Error('rewrite timed out');
    if (run.status !== 'succeeded') throw new Error(run.error || 'rewrite failed');
    const text = stripWrappingQuotes(run.result ?? '');
    if (!text) throw new Error('rewrite came back empty');
    return text;
  },

  /** Starts the attendee plan for an event; returns the root run id. The page offers it to
   * admins; the API admits any operator-workspace user, as for every node skill. */
  startAttendeeSearch: async (eventId: string): Promise<string> => {
    const started = await httpClient.post<{ run_id: string }>('/runs', {
      task: 'attendee-plan',
      params: { event_id: eventId },
    });
    const runId = started.data?.run_id;
    if (!runId) throw new Error('attendee search was not queued');
    return runId;
  },

  runStatus: async (statusUrl: string): Promise<RunOutcome> => {
    const run = await httpClient.get<{
      status: string;
      error: string | null;
      result: string | null;
      verdict: string | null;
      verdict_data: Record<string, unknown>;
    }>(statusUrl);
    return {
      status: run.data?.status ?? 'running',
      error: run.data?.error ?? null,
      result: run.data?.result ?? null,
      verdict: run.data?.verdict ?? null,
      verdictData: run.data?.verdict_data ?? {},
    };
  },

  /** Polls a run's status URL until it ends and resolves with it. Resolves null when the deadline
   * passes or the caller reports itself cancelled; the run itself continues server-side. */
  waitForRun: async (
    statusUrl: string,
    timeoutMs: number,
    { pollMs = RUN_POLL_MS, isCancelled }: { pollMs?: number; isCancelled?: () => boolean } = {},
  ): Promise<RunOutcome | null> => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, pollMs));
      if (isCancelled?.()) return null;
      let run: RunOutcome;
      try {
        run = await portalApi.runStatus(statusUrl);
      } catch (err) {
        // A flaky poll must not fail a long wait; a definite 4xx must.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (typeof status === 'number' && status < 500) throw err;
        console.error('run poll failed, retrying:', err);
        continue;
      }
      if (run.status === 'succeeded' || run.status === 'failed' || run.status === 'cancelled') {
        return run;
      }
    }
    return null;
  },

  /** Starts the conference lookup from what was typed, run by our team's workspace for
   * whoever asked; returns the URL to poll. */
  startEventLookup: async (input: EventLookupInput): Promise<string> => {
    const params = Object.fromEntries(
      Object.entries({
        name: input.name,
        city: input.city,
        country: input.country,
        date_start: input.dateStart,
        date_end: input.dateEnd,
        website: input.website,
      }).filter(([, v]) => (v ?? '').trim() !== ''),
    );
    const started = await httpClient.post<{ run_id: string }>('/portal/events/lookup', params);
    const runId = started.data?.run_id;
    if (!runId) throw new Error('conference lookup was not queued');
    return `/portal/events/lookup/${runId}`;
  },

  markReplyRead: async (replyId: string): Promise<void> => {
    await httpClient.post(`/portal/replies/${replyId}/read`);
  },

  /** Partial PATCH of client-owned reply state; omitted fields stay untouched. */
  updateReply: async (
    replyId: string,
    changes: { disposition?: string | null; triage_status?: CampaignReply['triageStatus'] },
  ): Promise<void> => {
    await httpClient.patch(`/portal/replies/${replyId}`, changes);
  },

  /** SKY-451: partial PATCH — sends only triage keys so disposition is untouched. */
  setReplyTriage: async (
    replyId: string, triageStatus: CampaignReply['triageStatus'],
  ): Promise<void> => portalApi.updateReply(replyId, { triage_status: triageStatus }),

  patchTamPerson: async (entityId: string, patch: { tier?: string | null; suppressed?: boolean }): Promise<void> => {
    await httpClient.patch(`/portal/tam/${entityId}`, patch);
  },

  suggestTamPerson: async (input: { display_name: string; linkedin_url?: string; note?: string }): Promise<void> => {
    await httpClient.post('/portal/tam/suggest', input);
  },

  /** SKY-499 client list upload. Multipart to the existing endpoint; the
   * backend dedupes (email > linkedin > domain > name) and reports per-row
   * outcomes — counts always reconcile with what actually happened. */
  uploadPeopleCsv: async (file: File, listName: string): Promise<UploadListResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('list_name', listName);
    form.append('kind', 'upload');
    const res = await httpClient.post<{
      results: { total: number; created: number; merged: number; failed: number };
      errors: { index: number | null; reason: string; row: number | null }[];
      lead_list_id: string | null;
    }>('/portal/tam/upload', form, {
      // The instance default is application/json, under which axios 1.x
      // JSON-stringifies FormData and drops the File — force multipart so
      // axios emits the real boundary header.
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return {
      created: res.data.results?.created ?? 0,
      merged: res.data.results?.merged ?? 0,
      failed: res.data.results?.failed ?? 0,
      total: res.data.results?.total ?? 0,
      errors: res.data.errors ?? [],
      leadListId: res.data.lead_list_id ?? null,
    };
  },

  /** SKY-478 inline correct control: persists as an attributed fact the
   * operator sees; the next brief refresh treats it as ground truth. */
  submitAccountCorrection: async (
    entityId: string,
    input: { claimExcerpt: string; correctionText: string },
  ): Promise<void> => {
    await httpClient.post(`/portal/accounts/${entityId}/correction`, {
      claim_excerpt: input.claimExcerpt,
      correction_text: input.correctionText,
    });
  },
};
